/**
 * OutputFormatsTemplateEditor — Full template editor panel.
 * Two-column layout: settings/columns on the left, live preview on the right.
 * Supports create and edit modes.
 *
 * Column transforms follow the TIC (Trade Image Capture) pattern:
 * select a transform type (Date Format, Number Format, Enum Map, Text Replace, Conditional)
 * then configure it inline below the row.
 *
 * Conditional columns are created via "+ Add Conditional Field" button.
 * They appear in the columns grid like any other column, but their transform
 * panel shows condition field, operator, value, and then/else outputs.
 * The columnId is auto-generated — users never see it.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { outputFormatsService } from '../../services/api/outputFormatsService';
import { groupingsService } from '../../services/api/groupingsService';
import { customReportService } from '../../services/api/customReportService';
import { GroupingsConfig } from '../../types/groupings';
import EnumMappingEditor from './EnumMappingEditor';
import TextReplaceBuilder from './TextReplaceBuilder';
import AlertModal from '../common/AlertModal';
import type {
  SourceType,
  OutputFormat,
  RowMode,
  OutputFormatTemplate,
  ColumnRule,
  EnumMapping,
  CalculatedColumn,
  ConditionalColumn,
  ConditionalOperator,
  ConcatenatedColumn,
  ConcatenatedPart,
  TradeEnrichmentConfig,
  SortSpec,
  CsvOptions,
  SourceSchema,
  SourceFieldDef,
  NullBehaviour,
  TransformStep,
  TransformStepType,
} from '../../types/outputFormats';
import { TRADE_FIELD_ENUMS, COMMODITY_FIELD_ENUMS } from '../../constants/tradeFields';
import './OutputFormats.css';

// Alias map: backend source schema may use different field names than the shared enum keys.
// Covers both camelCase (ACCOUNTING_ENTRIES trade enrichment) and PascalCase (TRADES source).
// FX enum keys differ from source schema names; commodity keys match directly.
const FX_FIELD_ALIASES: Record<string, string> = {
  productType: 'tradeType',
  ProductType: 'tradeType',
  Direction: 'direction',
  SettlementType: 'settlementType',
  FixingReference: 'fixingReference',
  OurParty: 'ourParty',
  Counterparty: 'CounterpartyName',  // FX source uses "Counterparty", dynamic enum key is "CounterpartyName"
};
// Commodity source schema fields already match COMMODITY_FIELD_ENUMS keys (PascalCase).
const COMMODITY_FIELD_ALIASES: Record<string, string> = {};

// Exported for CustomReportTemplateEditor (embedded mode)
export interface ColumnEditorRef {
  getColumnState: () => {
    columns: ColumnRule[];
    enumMappings: EnumMapping[];
    calculatedColumns: CalculatedColumn[];
    conditionalColumns: ConditionalColumn[];
    concatenatedColumns: ConcatenatedColumn[];
    sortBy: SortSpec[];
  };
  getSourceSchema: () => SourceSchema | null;
}

interface OutputFormatsTemplateEditorProps {
  clientId: string;
  sourceType: SourceType;
  templateId?: string; // undefined = create mode
  onSave: () => void;
  onCancel: () => void;
  // Embedded mode: renders only the Columns section, parent controls settings/filters/save
  embedded?: boolean;
  embeddedSchema?: SourceSchema;
  initialColumns?: ColumnRule[];
  initialEnumMappings?: EnumMapping[];
  initialCalculatedColumns?: CalculatedColumn[];
  initialConditionalColumns?: ConditionalColumn[];
  initialSortBy?: SortSpec[];
  // Asset class: determines which enum values are shown in ENUM_MAP transforms (KAN-267)
  assetClass?: 'FX' | 'COMMODITY';
}

// Default empty template for create mode
const DEFAULT_TEMPLATE: Omit<OutputFormatTemplate, 'templateId'> = {
  name: '',
  description: '',
  sourceType: 'ACCOUNTING_ENTRIES',
  outputFormat: 'CSV',
  rowMode: 'INDIVIDUAL',
  isDefault: false,
  csvOptions: {
    delimiter: ',',
    encoding: 'UTF-8',
    includeHeader: true,
    lineEnding: 'CRLF',
  },
  columns: [],
  enumMappings: [],
  calculatedColumns: [],
  conditionalColumns: [],
  concatenatedColumns: [],
  tradeEnrichment: {
    enabled: false,
    tradeIdField: 'tradeId',
    fields: [],
  },
  isActive: true,
};

// Conditional operator keys — labels resolved via i18n at render time
const CONDITIONAL_OPERATORS: ConditionalOperator[] = [
  'IS_NOT_NULL', 'IS_NULL', 'EQUALS', 'NOT_EQUALS', 'IN', 'NOT_IN', 'GREATER_THAN', 'LESS_THAN',
];

const OPERATORS_REQUIRING_VALUE: ConditionalOperator[] = ['EQUALS', 'NOT_EQUALS', 'IN', 'NOT_IN', 'GREATER_THAN', 'LESS_THAN'];

// Operators that accept multiple comma-separated values
const MULTI_VALUE_OPERATORS: ConditionalOperator[] = ['IN', 'NOT_IN'];

// Conditional config stored on EditorColumn (stripped on save, rebuilt into conditionalColumns)
// Transform fields mirror ConcatenatedPart — applied to FIELD/STATIC branch outputs.
type BranchOutput = {
  type: 'STATIC' | 'FIELD' | 'CONCATENATED';
  value?: string;
  parts?: ConcatenatedPart[];
  separator?: string;
  // Per-branch transforms (FIELD and STATIC outputs only; CONCATENATED uses per-part transforms)
  regexPattern?: string;
  regexReplacement?: string;
  dateFormat?: string;
  numberFormat?: string;
  textTransform?: string;
  enumMappingRef?: string;
};

interface CondBranch {
  conditionField: string;
  operator: ConditionalOperator;
  conditionValue?: string | null;
  output: BranchOutput;
}

interface CondConfig {
  branches: CondBranch[];
  elseOutput: BranchOutput;
}

/**
 * Migrate a ConditionalColumn (from Firestore / API) into the editor's CondConfig.
 * Handles both legacy flat format (conditionField/trueOutput/falseOutput) and
 * new branches format (branches[]/elseOutput).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const migrateCondColToConfig = (condCol: any): CondConfig => {
  if (condCol.branches && condCol.branches.length > 0) {
    // New format — use directly
    return {
      branches: condCol.branches.map((b: any) => ({
        conditionField: b.conditionField,
        operator: b.operator as ConditionalOperator,
        conditionValue: b.conditionValue,
        output: { ...b.output },
      })),
      elseOutput: { ...condCol.elseOutput },
    };
  }
  // Legacy flat format — wrap into single branch
  return {
    branches: [{
      conditionField: condCol.conditionField,
      operator: (condCol.operator || 'EQUALS') as ConditionalOperator,
      conditionValue: condCol.conditionValue,
      output: { ...condCol.trueOutput },
    }],
    elseOutput: { ...condCol.falseOutput },
  };
};

// Extended column type: adds optional _condConfig for conditional columns (local to editor)
interface EditorColumn extends ColumnRule {
  _condConfig?: CondConfig;
  _transforms?: TransformStep[];
}

// Transform type keys — labels resolved via i18n at render time
const TRANSFORM_TYPE_KEYS = ['DATE_FORMAT', 'NUMBER_FORMAT', 'ENUM_MAP', 'TEXT_REPLACE', 'TEXT_TRANSFORM', 'CONDITIONAL', 'MULTI_TRANSFORM'];

// Determine the active transform type from column fields
const getColumnTransformType = (col: EditorColumn): string | undefined => {
  if (col._condConfig) return 'CONDITIONAL';
  if (col.sourceField.startsWith('concat:')) return 'CONCATENATED';
  if (col._transforms && col._transforms.length > 0) return 'MULTI_TRANSFORM';
  if (col.enumMappingRef) return 'ENUM_MAP';
  if (col.dateFormat) return 'DATE_FORMAT';
  if (col.numberFormat) return 'NUMBER_FORMAT';
  if (col.regexPattern !== undefined || col.regexReplacement !== undefined) return 'TEXT_REPLACE';
  if (col.textTransform) return 'TEXT_TRANSFORM';
  return undefined;
};

// Detect the active per-branch transform type for FIELD/STATIC conditional branch outputs
const getBranchTransformType = (output: { regexPattern?: string; regexReplacement?: string; dateFormat?: string; numberFormat?: string; textTransform?: string; enumMappingRef?: string }): string => {
  if (output.enumMappingRef !== undefined) return 'ENUM_MAP';
  if (output.regexPattern !== undefined || output.regexReplacement !== undefined) return 'TEXT_REPLACE';
  if (output.textTransform) return 'TEXT_TRANSFORM';
  if (output.dateFormat) return 'DATE_FORMAT';
  if (output.numberFormat) return 'NUMBER_FORMAT';
  return '';
};

// Detect the active per-part transform type for concatenated column parts
const getPartTransformType = (part: ConcatenatedPart): string => {
  if (part.enumMappingRef !== undefined) return 'ENUM_MAP';
  if (part.regexPattern !== undefined || part.regexReplacement !== undefined) return 'TEXT_REPLACE';
  if (part.textTransform) return 'TEXT_TRANSFORM';
  if (part.dateFormat) return 'DATE_FORMAT';
  if (part.numberFormat) return 'NUMBER_FORMAT';
  return '';
};

const OutputFormatsTemplateEditor = React.forwardRef<ColumnEditorRef, OutputFormatsTemplateEditorProps>(({
  clientId,
  sourceType,
  templateId,
  onSave,
  onCancel,
  embedded,
  embeddedSchema,
  initialColumns,
  initialEnumMappings,
  initialCalculatedColumns,
  initialConditionalColumns,
  initialSortBy,
  assetClass = 'FX',
}, ref) => {
  const { t } = useTranslation();
  const isEditMode = !!templateId;

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('CSV');
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [csvOptions, setCsvOptions] = useState<CsvOptions>({
    delimiter: ',',
    encoding: 'UTF-8',
    includeHeader: true,
    lineEnding: 'CRLF',
  });
  const [rowMode, setRowMode] = useState<RowMode>('INDIVIDUAL');
  const [columns, setColumns] = useState<EditorColumn[]>([]);
  const [enumMappings, setEnumMappings] = useState<EnumMapping[]>([]);
  const [calculatedColumns, setCalculatedColumns] = useState<CalculatedColumn[]>([]);
  const [concatenatedColumns, setConcatenatedColumns] = useState<ConcatenatedColumn[]>([]);
  const [tradeEnrichment, setTradeEnrichment] = useState<TradeEnrichmentConfig>({
    enabled: false,
    tradeIdField: 'tradeId',
    fields: [],
  });
  const [sortBy, setSortBy] = useState<SortSpec[]>([]);

  // Source schema
  const [sourceSchema, setSourceSchema] = useState<SourceSchema | null>(null);

  // Groupings config (for dynamic enum values)
  const [groupingsConfig, setGroupingsConfig] = useState<GroupingsConfig | null>(null);

  // Dynamic enum values from filter-options endpoint (client-specific)
  const [ourPartyValues, setOurPartyValues] = useState<string[]>([]);
  const [counterpartyValues, setCounterpartyValues] = useState<string[]>([]);
  const [underlyingValues, setUnderlyingValues] = useState<string[]>([]);
  const [venueValues, setVenueValues] = useState<string[]>([]);
  const [workflowStateValues, setWorkflowStateValues] = useState<string[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [expandedColumnIdx, setExpandedColumnIdx] = useState<number | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [csvOptionsExpanded, setCsvOptionsExpanded] = useState(false);

  // Drag state
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  // Alert modal
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' });

  // Confirm modal (replaces window.confirm)
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  // Expose column state for embedded mode (parent reads via ref)
  React.useImperativeHandle(ref, () => ({
    getColumnState: () => {
      const conditionalCols: ConditionalColumn[] = [];
      const savedColumns: ColumnRule[] = columns.map((col, idx) => {
        const { _condConfig, _transforms, ...baseCol } = col;
        if (_condConfig) {
          const colId = `auto_${idx}`;
          conditionalCols.push({
            columnId: colId,
            name: baseCol.outputName,
            branches: _condConfig.branches,
            elseOutput: _condConfig.elseOutput,
          });
          return { ...baseCol, sourceField: `cond:${colId}` };
        }
        // Serialize multi-transform pipeline
        if (_transforms && _transforms.length > 0) {
          const first = _transforms[0];
          return {
            ...baseCol,
            transforms: _transforms,
            dateFormat: first.type === 'DATE_FORMAT' ? first.dateFormat : undefined,
            numberFormat: first.type === 'NUMBER_FORMAT' ? first.numberFormat : undefined,
            enumMappingRef: first.type === 'ENUM_MAP' ? first.enumMappingRef : undefined,
            regexPattern: first.type === 'TEXT_REPLACE' ? first.regexPattern : undefined,
            regexReplacement: first.type === 'TEXT_REPLACE' ? first.regexReplacement : undefined,
            textTransform: first.type === 'TEXT_TRANSFORM' ? first.textTransform : undefined,
          };
        }
        return baseCol;
      });
      return {
        columns: savedColumns,
        enumMappings,
        calculatedColumns,
        conditionalColumns: conditionalCols,
        concatenatedColumns,
        sortBy,
      };
    },
    getSourceSchema: () => sourceSchema,
  }), [columns, enumMappings, calculatedColumns, concatenatedColumns, sortBy, sourceSchema]);

  const showError = (message: string) => {
    setAlertModal({ isOpen: true, type: 'error', title: t('common.error'), message });
  };

  const showConfirm = (title: string, message: string, onConfirm: () => void) => {
    setConfirmModal({ isOpen: true, title, message, onConfirm });
  };

  // Load data on mount
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Embedded mode: only load source schema, don't load template.
      // When embedded, the parent provides the schema via embeddedSchema.
      // If not yet available (parent still loading), stay in loading state
      // and wait — don't fall back to the Output Formats service which
      // has no asset-class awareness and would return the wrong fields.
      if (embedded) {
        if (!embeddedSchema) return;
        const schema = embeddedSchema;
        setSourceSchema(schema);
        if (initialColumns && initialColumns.length > 0) {
          const condCols = initialConditionalColumns || [];
          const editorColumns: EditorColumn[] = initialColumns.map((col) => {
            const editorCol: EditorColumn = { ...col };
            if (col.sourceField.startsWith('cond:')) {
              const condId = col.sourceField.replace('cond:', '');
              const condCol = condCols.find((c) => c.columnId === condId);
              if (condCol) {
                editorCol._condConfig = migrateCondColToConfig(condCol);
              }
            }
            // Restore multi-transform pipeline if saved with 2+ steps
            if (col.transforms && col.transforms.length >= 2) {
              editorCol._transforms = col.transforms;
            }
            return editorCol;
          });
          setColumns(editorColumns);
          setEnumMappings(initialEnumMappings || []);
          setCalculatedColumns(initialCalculatedColumns || []);
          setSortBy(initialSortBy || []);
        } else {
          // Embedded create mode: start with empty columns (user adds from available fields)
          setColumns([]);
        }
        return;
      }

      // When editing, determine rowMode first to fetch correct schema
      let templateRowMode: RowMode = 'INDIVIDUAL';
      if (templateId) {
        const tpl = await outputFormatsService.getTemplate(clientId, templateId);
        templateRowMode = tpl.rowMode || 'INDIVIDUAL';
        setName(tpl.name);
        setDescription(tpl.description || '');
        setOutputFormat(tpl.outputFormat);
        setRowMode(templateRowMode);
        setIsDefault(tpl.isDefault);
        setIsActive(tpl.isActive);
        if (tpl.csvOptions) setCsvOptions(tpl.csvOptions);
        setEnumMappings(tpl.enumMappings || []);
        setCalculatedColumns(tpl.calculatedColumns || []);
        setConcatenatedColumns(tpl.concatenatedColumns || []);
        setTradeEnrichment(tpl.tradeEnrichment || {
          enabled: false,
          tradeIdField: 'tradeId',
          fields: [],
        });
        setSortBy(tpl.sortBy || []);

        // Decompose cond: columns — merge conditional config into each column's _condConfig
        // Restore multi-transform pipeline into _transforms editor state
        const condCols = tpl.conditionalColumns || [];
        const editorColumns: EditorColumn[] = (tpl.columns || []).map((col) => {
          const editorCol: EditorColumn = { ...col };
          if (col.sourceField.startsWith('cond:')) {
            const condId = col.sourceField.replace('cond:', '');
            const condCol = condCols.find((c) => c.columnId === condId);
            if (condCol) {
              editorCol._condConfig = migrateCondColToConfig(condCol);
            }
          }
          // Restore multi-transform pipeline if saved with 2+ steps
          if (col.transforms && col.transforms.length >= 2) {
            editorCol._transforms = col.transforms;
          }
          return editorCol;
        });
        setColumns(editorColumns);
      }

      // Load source schema with rowMode
      const schema = await outputFormatsService.getSourceSchema(clientId, sourceType, templateRowMode);
      setSourceSchema(schema);

      if (!templateId) {
        // Create mode: auto-populate columns from core source fields
        const defaultColumns: EditorColumn[] = schema.coreFields.map((f, i) => ({
          sourceField: f.field,
          outputName: f.label,
          order: i + 1,
          included: true,
        }));
        setColumns(defaultColumns);
      }
    } catch (err) {
      console.error('Error loading template data:', err);
      showError(t('outputFormats.loadError'));
    } finally {
      setLoading(false);
    }
  }, [clientId, sourceType, templateId, t, embedded, embeddedSchema, initialColumns, initialEnumMappings, initialCalculatedColumns, initialConditionalColumns, initialSortBy]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load groupings independently (for dynamic enum values like TIC)
  useEffect(() => {
    if (!clientId) return;
    groupingsService.getGroupings(clientId).then((groupings) => {
      if (groupings) setGroupingsConfig(groupings);
    }).catch((err) => {
      console.error('Failed to load groupings config:', err);
    });
  }, [clientId]);

  // Load dynamic enum values from filter-options (asset-class-aware — KAN-267)
  useEffect(() => {
    if (!clientId) return;
    customReportService.getFilterOptions(clientId, assetClass).then((opts) => {
      if (opts?.ourPartyValues?.length) setOurPartyValues(opts.ourPartyValues);
      if (opts?.counterparties?.length) setCounterpartyValues(opts.counterparties);
      if (opts?.workflowStates?.length) setWorkflowStateValues(opts.workflowStates);
      if (opts?.underlyings?.length) setUnderlyingValues(opts.underlyings);
      if (opts?.venues?.length) setVenueValues(opts.venues);
    }).catch((err) => {
      console.error('Failed to load filter options:', err);
    });
  }, [clientId, assetClass]);

  // Track unsaved changes
  useEffect(() => {
    if (!loading) {
      setHasUnsavedChanges(true);
    }
  }, [name, description, outputFormat, rowMode, isDefault, isActive, csvOptions, columns, enumMappings, calculatedColumns, concatenatedColumns, tradeEnrichment, sortBy]);

  // Build available source field options (always includes all trade fields)
  const sourceFieldOptions = useMemo(() => {
    const options: { value: string; label: string; type: string; group: string }[] = [];

    if (sourceSchema) {
      for (const f of sourceSchema.coreFields) {
        options.push({ value: f.field, label: f.label, type: f.type, group: 'Source Fields' });
      }
      // Grouped side fields
      if (rowMode === 'GROUPED' && sourceSchema.groupedFields) {
        for (const side of sourceSchema.groupedFields) {
          for (const f of side.fields) {
            options.push({ value: f.field, label: f.label, type: f.type, group: `${side.sideLabel} side` });
          }
        }
      }
      for (const f of sourceSchema.tradeFields) {
        options.push({ value: `trade:${f.field}`, label: f.label, type: f.type, group: 'Trade Fields' });
      }
      // Valuation fields (TRADES source type)
      if (sourceSchema.valuationFields) {
        for (const f of sourceSchema.valuationFields) {
          options.push({ value: f.field, label: f.label, type: f.type, group: 'Valuation Fields' });
        }
      }
    }

    // Calculated columns
    for (const calc of calculatedColumns) {
      options.push({ value: `calc:${calc.columnId}`, label: `Calc: ${calc.name}`, type: 'number', group: 'Calculated' });
    }

    // Concatenated columns
    for (const conc of concatenatedColumns) {
      options.push({ value: `concat:${conc.columnId}`, label: `Concat: ${conc.name}`, type: 'string', group: 'Concatenated' });
    }

    // Conditional columns (derived from editor columns with _condConfig)
    for (const col of columns) {
      if (col._condConfig && col.sourceField.startsWith('cond:')) {
        options.push({ value: col.sourceField, label: `Cond: ${col.outputName}`, type: 'string', group: 'Conditional' });
      }
    }

    return options;
  }, [sourceSchema, rowMode, calculatedColumns, concatenatedColumns, columns]);

  // Sort field options: only fields that are included in the report columns
  const sortFieldOptions = useMemo(() => {
    const includedFields = new Set(
      columns
        .filter((c) => c.included)
        .map((c) => c.sourceField),
    );
    return sourceFieldOptions.filter((o) => includedFields.has(o.value));
  }, [sourceFieldOptions, columns]);

  // Helper: get sort direction labels based on field type
  const getSortDirectionLabels = (fieldValue: string) => {
    const fieldDef = sourceFieldOptions.find((o) => o.value === fieldValue);
    const fieldType = fieldDef?.type || 'string';
    if (fieldType === 'number') {
      return {
        asc: t('outputFormats.sortBy.ascNumber', '1 → 9'),
        desc: t('outputFormats.sortBy.descNumber', '9 → 1'),
      };
    }
    if (fieldType === 'date') {
      return {
        asc: t('outputFormats.sortBy.ascDate', 'Old → New'),
        desc: t('outputFormats.sortBy.descDate', 'New → Old'),
      };
    }
    return {
      asc: t('outputFormats.sortBy.ascString', 'A → Z'),
      desc: t('outputFormats.sortBy.descString', 'Z → A'),
    };
  };

  // Select the correct enum registry and field alias map based on asset class (KAN-267)
  const fieldAliases = assetClass === 'COMMODITY' ? COMMODITY_FIELD_ALIASES : FX_FIELD_ALIASES;
  const baseFieldEnums = assetClass === 'COMMODITY' ? COMMODITY_FIELD_ENUMS : TRADE_FIELD_ENUMS;

  // Build dynamic enum values (base enums + grouping values from config + Our Party values)
  const allEnums = useMemo(() => {
    const enums: Record<string, { value: string; label: string }[]> = {};
    for (const [field, values] of Object.entries(baseFieldEnums)) {
      enums[field] = values.map((v) => ({ value: v.value, label: t(v.labelKey) }));
    }
    if (groupingsConfig?.grouping1Values?.length) {
      enums.grouping1 = groupingsConfig.grouping1Values
        .filter((v) => v.isActive)
        .map((v) => ({ value: v.name, label: v.name }));
    }
    if (groupingsConfig?.grouping2Values?.length) {
      enums.grouping2 = groupingsConfig.grouping2Values
        .filter((v) => v.isActive)
        .map((v) => ({ value: v.name, label: v.name }));
    }
    if (ourPartyValues.length) {
      enums.ourParty = ourPartyValues.map((v) => ({ value: v, label: v }));
    }
    // Dynamic enum values from filter-options (KAN-267)
    if (counterpartyValues.length) {
      enums.CounterpartyName = counterpartyValues.map((v) => ({ value: v, label: v }));
    }
    if (underlyingValues.length) {
      enums.UnderlyingName = underlyingValues.map((v) => ({ value: v, label: v }));
    }
    if (venueValues.length) {
      enums.VenueName = venueValues.map((v) => ({ value: v, label: v }));
    }
    if (workflowStateValues.length) {
      enums.workflowState = workflowStateValues.map((v) => ({ value: v, label: v }));
    }
    return enums;
  }, [baseFieldEnums, groupingsConfig, ourPartyValues, counterpartyValues, underlyingValues, venueValues, workflowStateValues, t]);

  // Live preview: output column names for included columns in order
  const previewHeaders = useMemo(() => {
    return columns
      .filter((c) => c.included)
      .sort((a, b) => a.order - b.order)
      .map((c) => c.outputName || c.sourceField);
  }, [columns]);

  // ========== Validation ==========

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!name.trim()) {
      errors.push(t('outputFormats.validationErrors.nameRequired'));
    }
    if (name.length > 100) {
      errors.push(t('outputFormats.validationErrors.nameTooLong'));
    }

    // Duplicate order values
    const orders = columns.map((c) => c.order);
    if (new Set(orders).size !== orders.length) {
      errors.push(t('outputFormats.validationErrors.duplicateOrder'));
    }

    // Orphaned enum mapping refs
    const definedMappingIds = new Set(enumMappings.map((m) => m.mappingId));
    for (const col of columns) {
      if (col.enumMappingRef && !definedMappingIds.has(col.enumMappingRef)) {
        errors.push(t('outputFormats.validationErrors.orphanedEnumRef', { ref: col.enumMappingRef }));
      }
    }

    // Regex pairs
    for (const col of columns) {
      if ((col.regexPattern && col.regexReplacement == null) || (!col.regexPattern && col.regexReplacement != null && col.regexReplacement !== '')) {
        errors.push(t('outputFormats.validationErrors.regexMissingReplacement'));
        break;
      }
    }

    // Conditional column validation (validates each branch)
    for (const col of columns) {
      if (col._condConfig) {
        for (let bIdx = 0; bIdx < col._condConfig.branches.length; bIdx++) {
          const branch = col._condConfig.branches[bIdx];
          const branchLabel = bIdx === 0 ? 'IF' : `ELSE IF #${bIdx + 1}`;
          if (!branch.conditionField) {
            errors.push(t('outputFormats.validation.conditionFieldRequired', { name: `${col.outputName} (${branchLabel})` }));
          }
          if (OPERATORS_REQUIRING_VALUE.includes(branch.operator) && !branch.conditionValue) {
            errors.push(t('outputFormats.validation.conditionValueRequired', { name: `${col.outputName} (${branchLabel})`, operator: t(`outputFormats.conditionalColumns.operators.${branch.operator}`) }));
          }
        }
      }
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // ========== Save ==========

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      // Rebuild conditionalColumns from columns with _condConfig
      // Serialize _transforms into transforms array for persistence
      const conditionalColumns: ConditionalColumn[] = [];
      const savedColumns: ColumnRule[] = columns.map((col, idx) => {
        const { _condConfig, _transforms, ...baseCol } = col;
        if (_condConfig) {
          const colId = `auto_${idx}`;
          conditionalColumns.push({
            columnId: colId,
            name: baseCol.outputName,
            branches: _condConfig.branches,
            elseOutput: _condConfig.elseOutput,
          });
          return { ...baseCol, sourceField: `cond:${colId}` };
        }
        // Serialize multi-transform pipeline
        if (_transforms && _transforms.length > 0) {
          // Write transforms array for new pipeline
          // Also populate flat fields from first step for backward compat
          const first = _transforms[0];
          return {
            ...baseCol,
            transforms: _transforms,
            dateFormat: first.type === 'DATE_FORMAT' ? first.dateFormat : undefined,
            numberFormat: first.type === 'NUMBER_FORMAT' ? first.numberFormat : undefined,
            enumMappingRef: first.type === 'ENUM_MAP' ? first.enumMappingRef : undefined,
            regexPattern: first.type === 'TEXT_REPLACE' ? first.regexPattern : undefined,
            regexReplacement: first.type === 'TEXT_REPLACE' ? first.regexReplacement : undefined,
            textTransform: first.type === 'TEXT_TRANSFORM' ? first.textTransform : undefined,
          };
        }
        return baseCol;
      });

      // Ensure trade enrichment includes fields referenced by concatenated parts
      const savedEnrichment = { ...tradeEnrichment };
      const enrichmentFields = new Set(savedEnrichment.fields);
      const collectConcatPartFields = (parts: ConcatenatedPart[]) => {
        for (const part of parts) {
          if (part.sourceField?.startsWith('trade:')) {
            enrichmentFields.add(part.sourceField.slice(6));
          }
          if (part.conditionField?.startsWith('trade:')) {
            enrichmentFields.add(part.conditionField.slice(6));
          }
        }
      };
      for (const cc of concatenatedColumns) {
        collectConcatPartFields(cc.parts);
      }
      // Also collect from conditional branch concatenated parts
      for (const condCol of conditionalColumns) {
        for (const branch of (condCol.branches || [])) {
          if (branch.output.type === 'CONCATENATED' && branch.output.parts) {
            collectConcatPartFields(branch.output.parts);
          }
        }
        if (condCol.elseOutput?.type === 'CONCATENATED' && condCol.elseOutput.parts) {
          collectConcatPartFields(condCol.elseOutput.parts);
        }
      }
      savedEnrichment.fields = Array.from(enrichmentFields);
      if (savedEnrichment.fields.length > 0) {
        savedEnrichment.enabled = true;
      }

      const templateData: Omit<OutputFormatTemplate, 'templateId'> = {
        name: name.trim(),
        description: description.trim() || undefined,
        sourceType,
        outputFormat,
        rowMode,
        isDefault,
        isActive,
        csvOptions: outputFormat === 'CSV' ? csvOptions : undefined,
        columns: savedColumns,
        enumMappings,
        calculatedColumns,
        conditionalColumns,
        concatenatedColumns,
        tradeEnrichment: savedEnrichment,
        sortBy: sortBy.length > 0 ? sortBy : undefined,
      };

      if (isEditMode && templateId) {
        await outputFormatsService.updateTemplate(clientId, templateId, templateData);
      } else {
        await outputFormatsService.createTemplate(clientId, templateData);
      }

      setHasUnsavedChanges(false);
      onSave();
    } catch (err) {
      console.error('Error saving template:', err);
      showError(t('outputFormats.saveError') + ': ' + String(err));
    } finally {
      setSaving(false);
    }
  };

  // ========== Deactivate ==========

  const handleDeactivate = () => {
    if (!templateId) return;
    showConfirm(
      t('outputFormats.deleteTemplate'),
      t('outputFormats.deleteConfirm'),
      async () => {
        setSaving(true);
        try {
          await outputFormatsService.deactivateTemplate(clientId, templateId);
          setHasUnsavedChanges(false);
          onSave();
        } catch (err) {
          console.error('Error deactivating template:', err);
          showError(String(err));
        } finally {
          setSaving(false);
        }
      }
    );
  };

  // ========== Cancel with unsaved changes check ==========

  const handleCancel = () => {
    if (hasUnsavedChanges && !loading) {
      showConfirm(
        t('common.unsavedChanges'),
        t('outputFormats.unsavedChangesMessage'),
        () => { onCancel(); }
      );
      return;
    }
    onCancel();
  };

  // ========== Row Mode change ==========

  const handleRowModeChange = async (newMode: RowMode) => {
    if (newMode === rowMode) return;

    const doSwitch = async () => {
      setRowMode(newMode);
      setColumns([]);
      setEnumMappings([]);
      // Reload schema with new rowMode
      try {
        const schema = await outputFormatsService.getSourceSchema(clientId, sourceType, newMode);
        setSourceSchema(schema);
        // Auto-populate default columns from the new schema
        const defaultColumns: EditorColumn[] = schema.coreFields.map((f, i) => ({
          sourceField: f.field,
          outputName: f.label,
          order: i + 1,
          included: true,
        }));
        setColumns(defaultColumns);
      } catch (err) {
        console.error('Error reloading schema:', err);
      }
    };

    if (columns.length > 0) {
      showConfirm(
        t('outputFormats.rowMode.label'),
        t('outputFormats.rowMode.changeModeWarning'),
        doSwitch,
      );
    } else {
      doSwitch();
    }
  };

  // ========== Column operations ==========

  const addFieldAsColumn = (fieldValue: string) => {
    const opt = sourceFieldOptions.find((o) => o.value === fieldValue);
    const nextOrder = columns.length > 0 ? Math.max(...columns.map((c) => c.order)) + 1 : 1;
    setColumns((prev) => [
      ...prev,
      {
        sourceField: fieldValue,
        outputName: opt?.label || fieldValue,
        order: nextOrder,
        included: true,
      },
    ]);

    // Auto-enable trade enrichment when a trade field is added
    if (fieldValue.startsWith('trade:')) {
      const tradeField = fieldValue.slice(6);
      setTradeEnrichment((prev) => ({
        ...prev,
        enabled: true,
        fields: prev.fields.includes(tradeField) ? prev.fields : [...prev.fields, tradeField],
      }));
    }
  };

  const addStaticColumn = () => {
    const nextOrder = columns.length > 0 ? Math.max(...columns.map((c) => c.order)) + 1 : 1;
    setColumns((prev) => [
      ...prev,
      {
        sourceField: 'static:',
        outputName: 'Static Field',
        order: nextOrder,
        included: true,
      },
    ]);
  };

  const addConditionalColumn = () => {
    const nextOrder = columns.length > 0 ? Math.max(...columns.map((c) => c.order)) + 1 : 1;
    const newCol: EditorColumn = {
      sourceField: '__conditional__', // placeholder, replaced on save with cond:auto_N
      outputName: t('outputFormats.conditionalColumns.newColumnName') || 'New Conditional',
      order: nextOrder,
      included: true,
      _condConfig: {
        branches: [{
          conditionField: '',
          operator: 'IS_NOT_NULL',
          conditionValue: null,
          output: { type: 'STATIC', value: '' },
        }],
        elseOutput: { type: 'STATIC', value: '' },
      },
    };
    setColumns((prev) => [...prev, newCol]);
    // Auto-expand the transform panel for the new column
    setExpandedColumnIdx(columns.length); // index of the newly added column
  };

  const addConcatenatedColumn = () => {
    const colId = `concat_${Date.now()}`;
    const defaultName = t('outputFormats.concatenatedColumns.sectionTitle');
    const newConc: ConcatenatedColumn = {
      columnId: colId,
      name: defaultName,
      separator: '',
      parts: [
        { type: 'FIELD', sourceField: '' },
      ],
    };
    setConcatenatedColumns((prev) => [...prev, newConc]);
    // Also add a column row referencing this concatenated column
    const nextOrder = columns.length > 0 ? Math.max(...columns.map((c) => c.order)) + 1 : 1;
    setColumns((prev) => [
      ...prev,
      {
        sourceField: `concat:${colId}`,
        outputName: defaultName,
        order: nextOrder,
        included: true,
      },
    ]);
    setExpandedColumnIdx(columns.length);
  };

  const updateConcatenatedColumn = (columnId: string, updates: Partial<ConcatenatedColumn>) => {
    setConcatenatedColumns((prev) =>
      prev.map((c) => (c.columnId === columnId ? { ...c, ...updates } : c))
    );
  };

  const removeConcatenatedColumn = (columnId: string) => {
    setConcatenatedColumns((prev) => prev.filter((c) => c.columnId !== columnId));
    // Also remove any column rows that reference this concat column
    setColumns((prev) => prev.filter((c) => c.sourceField !== `concat:${columnId}`));
  };

  const updateColumn = (idx: number, updates: Partial<EditorColumn>) => {
    setColumns((prev) =>
      prev.map((col, i) => (i === idx ? { ...col, ...updates } : col))
    );
  };

  const removeColumn = (idx: number) => {
    // Clean up enum mapping if this column had one
    const col = columns[idx];
    if (col.enumMappingRef) {
      const isReferencedElsewhere = columns.some((c, i) => i !== idx && c.enumMappingRef === col.enumMappingRef);
      if (!isReferencedElsewhere) {
        setEnumMappings((prev) => prev.filter((m) => m.mappingId !== col.enumMappingRef));
      }
    }
    if (expandedColumnIdx === idx) setExpandedColumnIdx(null);

    // Auto-clean concatenated column when a concat column is removed
    if (col.sourceField.startsWith('concat:')) {
      const concatId = col.sourceField.slice(7);
      setConcatenatedColumns((prev) => prev.filter((c) => c.columnId !== concatId));
    }

    // Auto-clean trade enrichment when a trade field column is removed
    // Keep the field if it's still referenced by other columns or concatenated parts
    if (col.sourceField.startsWith('trade:')) {
      const tradeField = col.sourceField.slice(6);
      const otherTradeColumns = columns.filter((c, i) => i !== idx && c.sourceField.startsWith('trade:'));
      const remainingTradeFields = new Set(otherTradeColumns.map((c) => c.sourceField.slice(6)));
      // Also check concatenated parts for trade field references
      for (const cc of concatenatedColumns) {
        for (const p of cc.parts) {
          if (p.sourceField?.startsWith('trade:')) remainingTradeFields.add(p.sourceField.slice(6));
          if (p.conditionField?.startsWith('trade:')) remainingTradeFields.add(p.conditionField.slice(6));
        }
      }
      setTradeEnrichment((prev) => {
        const fields = prev.fields.filter((f) => f !== tradeField || remainingTradeFields.has(f));
        return { ...prev, enabled: fields.length > 0, fields };
      });
    }

    setColumns((prev) => {
      const filtered = prev.filter((_, i) => i !== idx);
      return filtered.map((c, i) => ({ ...c, order: i + 1 }));
    });
  };

  // Available fields: all fields always available (same field can be used in multiple columns)
  const availableCoreFields = useMemo(() => {
    return sourceFieldOptions.filter((o) => o.group === 'Source Fields');
  }, [sourceFieldOptions]);

  const availableSideFields = useMemo(() => {
    if (rowMode !== 'GROUPED' || !sourceSchema?.groupedFields) return [];
    const sideGroups: { sideName: string; sideLabel: string; fields: typeof sourceFieldOptions }[] = [];
    for (const side of sourceSchema.groupedFields) {
      sideGroups.push({
        sideName: side.sideName,
        sideLabel: side.sideLabel,
        fields: sourceFieldOptions.filter((o) => o.group === `${side.sideLabel} side`),
      });
    }
    return sideGroups;
  }, [sourceFieldOptions, rowMode, sourceSchema]);

  const availableTradeFields = useMemo(() => {
    return sourceFieldOptions.filter((o) => o.group === 'Trade Fields');
  }, [sourceFieldOptions]);

  const availableValuationFields = useMemo(() => {
    return sourceFieldOptions.filter((o) => o.group === 'Valuation Fields');
  }, [sourceFieldOptions]);

  // Drag-and-drop handlers (HTML5 native)
  const handleDragStart = (idx: number) => {
    dragItem.current = idx;
  };

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    dragOverItem.current = idx;
  };

  const handleDrop = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) return;

    const reordered = [...columns];
    const [dragged] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, dragged);

    const withNewOrder = reordered.map((col, i) => ({ ...col, order: i + 1 }));
    setColumns(withNewOrder);

    dragItem.current = null;
    dragOverItem.current = null;
  };

  // ========== Transform operations (TIC-style) ==========

  const applyTransformType = (idx: number, newType: string) => {
    const col = columns[idx];

    // Clear all transform fields
    const cleared: Partial<EditorColumn> = {
      dateFormat: undefined,
      numberFormat: undefined,
      regexPattern: undefined,
      regexReplacement: undefined,
      enumMappingRef: undefined,
      textTransform: undefined,
      _condConfig: undefined,
    };

    // Remove old enum mapping if switching away from ENUM_MAP
    if (col.enumMappingRef && newType !== 'ENUM_MAP') {
      const isReferencedElsewhere = columns.some((c, i) => i !== idx && c.enumMappingRef === col.enumMappingRef);
      if (!isReferencedElsewhere) {
        setEnumMappings((prev) => prev.filter((m) => m.mappingId !== col.enumMappingRef));
      }
    }

    // Set defaults for new type
    switch (newType) {
      case 'DATE_FORMAT':
        cleared.dateFormat = 'DD/MM/YYYY';
        cleared._transforms = undefined;
        break;
      case 'NUMBER_FORMAT':
        cleared.numberFormat = '1,234.56';
        cleared._transforms = undefined;
        break;
      case 'ENUM_MAP': {
        cleared._transforms = undefined;
        if (col.enumMappingRef && newType === 'ENUM_MAP') {
          // Keep existing mapping
          cleared.enumMappingRef = col.enumMappingRef;
        } else {
          const id = `mapping_${Date.now()}`;
          setEnumMappings((prev) => [...prev, {
            mappingId: id,
            name: col.outputName || col.sourceField,
            fallback: null,
            entries: [] as { from: string; to: string }[],
          }]);
          cleared.enumMappingRef = id;
        }
        break;
      }
      case 'TEXT_REPLACE':
        cleared.regexPattern = '';
        cleared.regexReplacement = '';
        cleared._transforms = undefined;
        break;
      case 'TEXT_TRANSFORM':
        cleared.textTransform = 'UPPERCASE';
        cleared._transforms = undefined;
        break;
      case 'CONDITIONAL':
        cleared._transforms = undefined;
        cleared._condConfig = {
          conditionField: '',
          operator: 'IS_NOT_NULL',
          conditionValue: null,
          trueOutput: { type: 'STATIC', value: '' },
          falseOutput: { type: 'STATIC', value: '' },
        };
        break;
      case 'MULTI_TRANSFORM': {
        // Convert existing single transform into first step, add an empty second step
        const existingSteps: TransformStep[] = [];
        if (col.enumMappingRef) {
          existingSteps.push({ type: 'ENUM_MAP', enumMappingRef: col.enumMappingRef });
        } else if (col.dateFormat) {
          existingSteps.push({ type: 'DATE_FORMAT', dateFormat: col.dateFormat });
        } else if (col.numberFormat) {
          existingSteps.push({ type: 'NUMBER_FORMAT', numberFormat: col.numberFormat });
        } else if (col.regexPattern !== undefined || col.regexReplacement !== undefined) {
          existingSteps.push({ type: 'TEXT_REPLACE', regexPattern: col.regexPattern, regexReplacement: col.regexReplacement });
        } else if (col.textTransform) {
          existingSteps.push({ type: 'TEXT_TRANSFORM', textTransform: col.textTransform });
        }
        // Add an empty second step to get the user started
        existingSteps.push({ type: 'TEXT_REPLACE', regexPattern: '', regexReplacement: '' });
        cleared._transforms = existingSteps;
        break;
      }
    }

    updateColumn(idx, cleared);
  };

  const removeTransformFromColumn = (idx: number) => {
    const col = columns[idx];
    // Clean up enum mappings from flat fields
    if (col.enumMappingRef) {
      const isReferencedElsewhere = columns.some((c, i) => i !== idx && c.enumMappingRef === col.enumMappingRef);
      if (!isReferencedElsewhere) {
        setEnumMappings((prev) => prev.filter((m) => m.mappingId !== col.enumMappingRef));
      }
    }
    // Clean up enum mappings from _transforms steps
    if (col._transforms) {
      for (const step of col._transforms) {
        if (step.enumMappingRef) {
          const isReferencedElsewhere = columns.some((c, i) => {
            if (i === idx) return false;
            if (c.enumMappingRef === step.enumMappingRef) return true;
            return c._transforms?.some((s) => s.enumMappingRef === step.enumMappingRef);
          });
          if (!isReferencedElsewhere) {
            setEnumMappings((prev) => prev.filter((m) => m.mappingId !== step.enumMappingRef));
          }
        }
      }
    }
    updateColumn(idx, {
      dateFormat: undefined,
      numberFormat: undefined,
      regexPattern: undefined,
      regexReplacement: undefined,
      enumMappingRef: undefined,
      textTransform: undefined,
      _condConfig: undefined,
      _transforms: undefined,
    });
  };

  // ========== Enum Mapping operations ==========

  const updateEnumMapping = (updated: EnumMapping) => {
    setEnumMappings((prev) =>
      prev.map((m) => (m.mappingId === updated.mappingId ? updated : m))
    );
  };

  // ========== Transform config renderer (TIC-style) ==========

  const renderTransformConfig = (col: EditorColumn, idx: number, transformType: string) => {
    switch (transformType) {
      case 'DATE_FORMAT':
        return (
          <div className="transform-config-editor">
            <div className="config-row">
              <label>{t('outputFormats.columns.dateFormat')}:</label>
              <select
                className="form-select form-select-sm"
                value={col.dateFormat || ''}
                onChange={(e) => updateColumn(idx, { dateFormat: e.target.value || undefined })}
              >
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                <option value="DD.MM.YYYY">DD.MM.YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="MM.DD.YYYY">MM.DD.YYYY</option>
                <option value="YYYYMMDD">YYYYMMDD</option>
                <option value="DDMMYYYY">DDMMYYYY</option>
              </select>
            </div>
          </div>
        );
      case 'NUMBER_FORMAT':
        return (
          <div className="transform-config-editor">
            <div className="config-row">
              <label>{t('outputFormats.columns.numberFormat')}:</label>
              <select
                className="form-select form-select-sm"
                value={col.numberFormat || ''}
                onChange={(e) => updateColumn(idx, { numberFormat: e.target.value || undefined })}
              >
                <option value="1,234.56">1,234.56</option>
                <option value="1.234,56">1.234,56</option>
                <option value="1234.56">1234.56</option>
                <option value="1234,56">1234,56</option>
                <option value="1234">1234</option>
              </select>
            </div>
          </div>
        );
      case 'ENUM_MAP': {
        const mapping = enumMappings.find((m) => m.mappingId === col.enumMappingRef);
        if (!mapping) return null;

        // Resolve field name (strip trade: prefix, apply aliases) to look up known enums
        const rawFieldName = col.sourceField.startsWith('trade:')
          ? col.sourceField.slice(6)
          : col.sourceField;
        const enumKey = fieldAliases[rawFieldName] || rawFieldName;
        const knownEnumValues = allEnums[enumKey];

        if (knownEnumValues) {
          // Structured editor: show each FX+ value with input for output value
          // entries use { from: fxPlusValue, to: outputValue }
          const entryLookup: Record<string, string> = {};
          mapping.entries.forEach((e) => {
            if (e.from) entryLookup[e.from] = e.to;
          });

          return (
            <div className="transform-config-editor">
              <div className="of-enum-table">
                <div className="of-enum-header">
                  <span>{t('outputFormats.enumMapping.fxPlusValue')}</span>
                  <span>{t('outputFormats.enumMapping.outputValue')}</span>
                </div>
                {knownEnumValues.map((enumVal) => (
                  <div key={enumVal.value} className="of-enum-row">
                    <span className="of-enum-fxplus">
                      {enumVal.label} <span className="of-enum-code">({enumVal.value})</span>
                    </span>
                    <input
                      type="text"
                      className="form-input config-input"
                      value={entryLookup[enumVal.value] || ''}
                      onChange={(e) => {
                        const newEntries = knownEnumValues.map((ev) => ({
                          from: ev.value,
                          to: ev.value === enumVal.value ? e.target.value : (entryLookup[ev.value] || ''),
                        }));
                        // Preserve any extra entries not in knownEnumValues
                        const knownFroms = new Set(knownEnumValues.map((ev) => ev.value));
                        const extraEntries = mapping.entries.filter((entry) => !knownFroms.has(entry.from));
                        updateEnumMapping({ ...mapping, entries: [...newEntries, ...extraEntries] });
                      }}
                      placeholder={t('outputFormats.enumMapping.outputValuePlaceholder')}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        }

        // Fallback: generic from/to editor for fields without known enums
        return (
          <EnumMappingEditor
            mapping={mapping}
            onChange={updateEnumMapping}
            onClose={() => setExpandedColumnIdx(null)}
          />
        );
      }
      case 'TEXT_REPLACE':
        return (
          <TextReplaceBuilder
            regexPattern={col.regexPattern || ''}
            regexReplacement={col.regexReplacement || ''}
            onChange={(pattern, replacement) => updateColumn(idx, { regexPattern: pattern, regexReplacement: replacement })}
          />
        );
      case 'TEXT_TRANSFORM':
        return (
          <div className="transform-config-editor">
            <div className="config-row">
              <label>{t('outputFormats.transforms.TEXT_TRANSFORM')}:</label>
              <select
                className="form-select form-select-sm"
                value={col.textTransform || 'UPPERCASE'}
                onChange={(e) => updateColumn(idx, { textTransform: e.target.value })}
              >
                <option value="UPPERCASE">{t('outputFormats.transforms.uppercase')}</option>
                <option value="LOWERCASE">{t('outputFormats.transforms.lowercase')}</option>
              </select>
            </div>
          </div>
        );
      case 'MULTI_TRANSFORM': {
        const steps = col._transforms || [];
        const STEP_TYPE_KEYS: TransformStepType[] = ['DATE_FORMAT', 'NUMBER_FORMAT', 'ENUM_MAP', 'TEXT_REPLACE', 'TEXT_TRANSFORM'];

        const updateStep = (stepIdx: number, updates: Partial<TransformStep>) => {
          const newSteps = [...steps];
          newSteps[stepIdx] = { ...newSteps[stepIdx], ...updates };
          updateColumn(idx, { _transforms: newSteps });
        };

        const removeStep = (stepIdx: number) => {
          // Clean up enum mapping if needed
          const step = steps[stepIdx];
          if (step.enumMappingRef) {
            const isReferencedElsewhere =
              columns.some((c, i) => {
                if (i === idx) {
                  return (c._transforms || []).some((s, si) => si !== stepIdx && s.enumMappingRef === step.enumMappingRef);
                }
                return c.enumMappingRef === step.enumMappingRef ||
                  (c._transforms || []).some((s) => s.enumMappingRef === step.enumMappingRef);
              });
            if (!isReferencedElsewhere) {
              setEnumMappings((prev) => prev.filter((m) => m.mappingId !== step.enumMappingRef));
            }
          }
          const newSteps = steps.filter((_, i) => i !== stepIdx);
          if (newSteps.length === 0) {
            // No steps left — revert to no transform
            removeTransformFromColumn(idx);
          } else if (newSteps.length === 1) {
            // Single step — revert to single-transform mode
            const single = newSteps[0];
            updateColumn(idx, {
              _transforms: undefined,
              dateFormat: single.type === 'DATE_FORMAT' ? single.dateFormat : undefined,
              numberFormat: single.type === 'NUMBER_FORMAT' ? single.numberFormat : undefined,
              enumMappingRef: single.type === 'ENUM_MAP' ? single.enumMappingRef : undefined,
              regexPattern: single.type === 'TEXT_REPLACE' ? single.regexPattern : undefined,
              regexReplacement: single.type === 'TEXT_REPLACE' ? single.regexReplacement : undefined,
              textTransform: single.type === 'TEXT_TRANSFORM' ? single.textTransform : undefined,
            });
          } else {
            updateColumn(idx, { _transforms: newSteps });
          }
        };

        const addStep = () => {
          const newSteps = [...steps, { type: 'TEXT_REPLACE' as TransformStepType, regexPattern: '', regexReplacement: '' }];
          updateColumn(idx, { _transforms: newSteps });
        };

        const changeStepType = (stepIdx: number, newStepType: TransformStepType) => {
          const oldStep = steps[stepIdx];
          // Clean up old enum mapping
          if (oldStep.enumMappingRef && newStepType !== 'ENUM_MAP') {
            const isReferencedElsewhere =
              columns.some((c, i) => {
                if (i === idx) {
                  return (c._transforms || []).some((s, si) => si !== stepIdx && s.enumMappingRef === oldStep.enumMappingRef);
                }
                return c.enumMappingRef === oldStep.enumMappingRef ||
                  (c._transforms || []).some((s) => s.enumMappingRef === oldStep.enumMappingRef);
              });
            if (!isReferencedElsewhere) {
              setEnumMappings((prev) => prev.filter((m) => m.mappingId !== oldStep.enumMappingRef));
            }
          }
          const newStep: TransformStep = { type: newStepType };
          switch (newStepType) {
            case 'DATE_FORMAT': newStep.dateFormat = 'DD/MM/YYYY'; break;
            case 'NUMBER_FORMAT': newStep.numberFormat = '1,234.56'; break;
            case 'ENUM_MAP': {
              const id = `mapping_${Date.now()}_${stepIdx}`;
              setEnumMappings((prev) => [...prev, {
                mappingId: id,
                name: col.outputName || col.sourceField,
                fallback: null,
                entries: [] as { from: string; to: string }[],
              }]);
              newStep.enumMappingRef = id;
              break;
            }
            case 'TEXT_REPLACE': newStep.regexPattern = ''; newStep.regexReplacement = ''; break;
            case 'TEXT_TRANSFORM': newStep.textTransform = 'UPPERCASE'; break;
          }
          const newSteps = [...steps];
          newSteps[stepIdx] = newStep;
          updateColumn(idx, { _transforms: newSteps });
        };

        const renderStepConfig = (step: TransformStep, stepIdx: number) => {
          switch (step.type) {
            case 'DATE_FORMAT':
              return (
                <div className="config-row">
                  <label>{t('outputFormats.columns.dateFormat')}:</label>
                  <select
                    className="form-select form-select-sm"
                    value={step.dateFormat || ''}
                    onChange={(e) => updateStep(stepIdx, { dateFormat: e.target.value || undefined })}
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                    <option value="DD.MM.YYYY">DD.MM.YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="MM.DD.YYYY">MM.DD.YYYY</option>
                    <option value="YYYYMMDD">YYYYMMDD</option>
                    <option value="DDMMYYYY">DDMMYYYY</option>
                  </select>
                </div>
              );
            case 'NUMBER_FORMAT':
              return (
                <div className="config-row">
                  <label>{t('outputFormats.columns.numberFormat')}:</label>
                  <select
                    className="form-select form-select-sm"
                    value={step.numberFormat || ''}
                    onChange={(e) => updateStep(stepIdx, { numberFormat: e.target.value || undefined })}
                  >
                    <option value="1,234.56">1,234.56</option>
                    <option value="1.234,56">1.234,56</option>
                    <option value="1234.56">1234.56</option>
                    <option value="1234,56">1234,56</option>
                    <option value="1234">1234</option>
                  </select>
                </div>
              );
            case 'ENUM_MAP': {
              const mapping = enumMappings.find((m) => m.mappingId === step.enumMappingRef);
              if (!mapping) return <div className="config-row"><label>No mapping configured</label></div>;

              const rawFieldName = col.sourceField.startsWith('trade:')
                ? col.sourceField.slice(6)
                : col.sourceField;
              const enumKey = fieldAliases[rawFieldName] || rawFieldName;
              const knownEnumValues = allEnums[enumKey];

              if (knownEnumValues) {
                const entryLookup: Record<string, string> = {};
                mapping.entries.forEach((e) => { if (e.from) entryLookup[e.from] = e.to; });
                return (
                  <div className="of-enum-table">
                    <div className="of-enum-header">
                      <span>{t('outputFormats.enumMapping.fxPlusValue')}</span>
                      <span>{t('outputFormats.enumMapping.outputValue')}</span>
                    </div>
                    {knownEnumValues.map((enumVal) => (
                      <div key={enumVal.value} className="of-enum-row">
                        <span className="of-enum-fxplus">
                          {enumVal.label} <span className="of-enum-code">({enumVal.value})</span>
                        </span>
                        <input
                          type="text"
                          className="form-input config-input"
                          value={entryLookup[enumVal.value] || ''}
                          onChange={(e) => {
                            const newEntries = knownEnumValues.map((ev) => ({
                              from: ev.value,
                              to: ev.value === enumVal.value ? e.target.value : (entryLookup[ev.value] || ''),
                            }));
                            const knownFroms = new Set(knownEnumValues.map((ev) => ev.value));
                            const extraEntries = mapping.entries.filter((entry) => !knownFroms.has(entry.from));
                            updateEnumMapping({ ...mapping, entries: [...newEntries, ...extraEntries] });
                          }}
                          placeholder={t('outputFormats.enumMapping.outputValuePlaceholder')}
                        />
                      </div>
                    ))}
                  </div>
                );
              }
              return (
                <EnumMappingEditor
                  mapping={mapping}
                  onChange={updateEnumMapping}
                  onClose={() => setExpandedColumnIdx(null)}
                />
              );
            }
            case 'TEXT_REPLACE':
              return (
                <TextReplaceBuilder
                  regexPattern={step.regexPattern || ''}
                  regexReplacement={step.regexReplacement || ''}
                  onChange={(pattern, replacement) => updateStep(stepIdx, { regexPattern: pattern, regexReplacement: replacement })}
                />
              );
            case 'TEXT_TRANSFORM':
              return (
                <div className="config-row">
                  <label>{t('outputFormats.transforms.TEXT_TRANSFORM')}:</label>
                  <select
                    className="form-select form-select-sm"
                    value={step.textTransform || 'UPPERCASE'}
                    onChange={(e) => updateStep(stepIdx, { textTransform: e.target.value })}
                  >
                    <option value="UPPERCASE">{t('outputFormats.transforms.uppercase')}</option>
                    <option value="LOWERCASE">{t('outputFormats.transforms.lowercase')}</option>
                  </select>
                </div>
              );
            default:
              return null;
          }
        };

        return (
          <div className="transform-config-editor">
            {steps.map((step, stepIdx) => (
              <div key={stepIdx} className="multi-transform-step" style={{ borderBottom: '1px solid var(--border-color, #e0e0e0)', paddingBottom: '8px', marginBottom: '8px' }}>
                <div className="config-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: 600, minWidth: '20px' }}>{stepIdx + 1}.</span>
                  <select
                    className="form-select form-select-sm"
                    value={step.type}
                    onChange={(e) => changeStepType(stepIdx, e.target.value as TransformStepType)}
                    style={{ flex: 1 }}
                  >
                    {STEP_TYPE_KEYS.map((k) => (
                      <option key={k} value={k}>
                        {t(`outputFormats.transforms.${k}`, k)}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn-icon"
                    onClick={() => removeStep(stepIdx)}
                    title={t('common.remove') || 'Remove'}
                    type="button"
                  >
                    ×
                  </button>
                </div>
                {renderStepConfig(step, stepIdx)}
              </div>
            ))}
            <button
              type="button"
              className="btn btn-sm btn-outline"
              onClick={addStep}
              style={{ marginTop: '4px' }}
            >
              + {t('outputFormats.transforms.addStep') || 'Add transform'}
            </button>
          </div>
        );
      }
      case 'CONDITIONAL': {
        const config = col._condConfig;
        if (!config) return null;

        const updateCondConfig = (updates: Partial<CondConfig>) => {
          updateColumn(idx, { _condConfig: { ...config, ...updates } });
        };

        // Helper to update a specific branch
        const updateBranchAtIndex = (bIdx: number, updates: Partial<CondBranch>) => {
          const newBranches = [...config.branches];
          newBranches[bIdx] = { ...newBranches[bIdx], ...updates };
          updateCondConfig({ branches: newBranches });
        };

        // Add ELSE IF branch
        const addBranch = () => {
          updateCondConfig({
            branches: [...config.branches, {
              conditionField: config.branches[0]?.conditionField || '',
              operator: 'EQUALS' as ConditionalOperator,
              conditionValue: '',
              output: { type: 'STATIC', value: '' },
            }],
          });
        };

        // Remove branch (only for idx > 0)
        const removeBranch = (bIdx: number) => {
          const newBranches = config.branches.filter((_, i) => i !== bIdx);
          updateCondConfig({ branches: newBranches });
        };

        // Render condition controls for a branch
        const renderConditionControls = (branch: CondBranch, bIdx: number) => {
          const onUpdateBranch = (updates: Partial<CondBranch>) => updateBranchAtIndex(bIdx, updates);
          const condFieldDef = sourceFieldOptions.find((f) => f.value === branch.conditionField);
          const isDateField = condFieldDef?.type === 'date';
          const condRawField = (branch.conditionField || '').startsWith('trade:')
            ? (branch.conditionField || '').slice(6)
            : (branch.conditionField || '');
          const condEnumKey = fieldAliases[condRawField] || condRawField;
          const condEnumVals = allEnums[condEnumKey];
          const isMulti = MULTI_VALUE_OPERATORS.includes(branch.operator);
          const selectedValues = (branch.conditionValue || '').split(',').filter(Boolean);
          const toggleValue = (val: string) => {
            const set = new Set(selectedValues);
            if (set.has(val)) set.delete(val); else set.add(val);
            onUpdateBranch({ conditionValue: Array.from(set).join(',') });
          };

          return (
            <>
              {/* Condition field selector */}
              <div className="config-row">
                <label>{t('outputFormats.conditionalColumns.conditionField')}:</label>
                <select
                  className="form-select form-select-sm"
                  value={branch.conditionField}
                  onChange={(e) => onUpdateBranch({ conditionField: e.target.value })}
                >
                  <option value="">-- {t('outputFormats.conditionalColumns.selectField') || 'Select field'} --</option>
                  {sourceFieldOptions.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>

              {/* Operator */}
              <div className="config-row">
                <label>{t('outputFormats.conditionalColumns.operator')}:</label>
                <select
                  className="form-select form-select-sm"
                  value={branch.operator}
                  onChange={(e) => onUpdateBranch({ operator: e.target.value as ConditionalOperator })}
                >
                  {CONDITIONAL_OPERATORS.map((op) => (
                    <option key={op} value={op}>{t(`outputFormats.conditionalColumns.operators.${op}`)}</option>
                  ))}
                </select>
              </div>

              {/* Condition value (only for operators that need it) */}
              {OPERATORS_REQUIRING_VALUE.includes(branch.operator) && (
                <div className="config-row">
                  <label>{t('outputFormats.conditionalColumns.conditionValue')}:</label>
                  {isMulti && condEnumVals ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', alignItems: 'center' }}>
                      {condEnumVals.map((ev) => (
                        <label key={ev.value} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={selectedValues.includes(ev.value)}
                            onChange={() => toggleValue(ev.value)}
                          />
                          {ev.label}
                        </label>
                      ))}
                    </div>
                  ) : isMulti ? (
                    <input
                      type="text"
                      className="form-input config-input"
                      value={branch.conditionValue || ''}
                      onChange={(e) => onUpdateBranch({ conditionValue: e.target.value })}
                      placeholder={t('outputFormats.conditionalColumns.commaSeparatedValues')}
                    />
                  ) : condEnumVals ? (
                    <select
                      className="form-select form-select-sm"
                      value={branch.conditionValue || ''}
                      onChange={(e) => onUpdateBranch({ conditionValue: e.target.value })}
                    >
                      <option value="">--</option>
                      {condEnumVals.map((ev) => (
                        <option key={ev.value} value={ev.value}>{ev.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={isDateField ? 'date' : 'text'}
                      className="form-input config-input"
                      value={branch.conditionValue || ''}
                      onChange={(e) => onUpdateBranch({ conditionValue: e.target.value })}
                      placeholder={isDateField ? 'YYYY-MM-DD' : t('outputFormats.conditionalColumns.conditionValue')}
                    />
                  )}
                </div>
              )}
            </>
          );
        };

        // Render an output block (STATIC / FIELD / CONCATENATED) — used for branch outputs and else
        const renderBranchOutput = (
          labelKey: string,
          output: BranchOutput,
          onPartialUpdate: (updates: Partial<BranchOutput>) => void,
          onFullReplace: (newOutput: BranchOutput) => void,
        ) => {
          const updateBranchCb = onPartialUpdate;
          const updateConcatPart = (pIdx: number, updates: Partial<ConcatenatedPart>) => {
            const newParts = [...(output.parts || [])];
            newParts[pIdx] = { ...newParts[pIdx], ...updates };
            updateBranchCb({ parts: newParts });
            // Auto-enable trade enrichment for trade: fields
            const fieldsToCheck = [updates.sourceField].filter(Boolean) as string[];
            for (const fv of fieldsToCheck) {
              if (fv.startsWith('trade:')) {
                const tradeField = fv.slice(6);
                setTradeEnrichment((prev) => ({
                  ...prev,
                  enabled: true,
                  fields: prev.fields.includes(tradeField) ? prev.fields : [...prev.fields, tradeField],
                }));
              }
            }
          };
          const removeConcatPart = (pIdx: number) => {
            // Clean up orphaned enum mapping when removing a part
            const removedPart = (output.parts || [])[pIdx];
            if (removedPart?.enumMappingRef) {
              const mappingId = removedPart.enumMappingRef;
              const isReferencedElsewhere =
                columns.some((c) => c.enumMappingRef === mappingId) ||
                concatenatedColumns.some((cc) =>
                  cc.parts.some((p) => p.enumMappingRef === mappingId)
                );
              if (!isReferencedElsewhere) {
                setEnumMappings((prev) => prev.filter((m) => m.mappingId !== mappingId));
              }
            }
            const newParts = (output.parts || []).filter((_, i) => i !== pIdx);
            updateBranchCb({ parts: newParts.length > 0 ? newParts : [{ type: 'FIELD', sourceField: '' }] });
          };
          const addConcatPart = () => {
            updateBranchCb({ parts: [...(output.parts || []), { type: 'FIELD', sourceField: '' }] });
          };

          return (
            <div className="config-row">
              <label>{t(labelKey)}:</label>
              <div className="of-cond-output" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                <select
                  className="form-select form-select-sm"
                  value={output.type}
                  onChange={(e) => {
                    const newType = e.target.value as 'STATIC' | 'FIELD' | 'CONCATENATED';
                    if (newType === 'CONCATENATED') {
                      onFullReplace({ type: 'CONCATENATED', parts: [{ type: 'FIELD', sourceField: '' }], separator: '' });
                    } else {
                      onFullReplace({ type: newType, value: '' });
                    }
                  }}
                  style={{ minWidth: '120px', maxWidth: '140px' }}
                >
                        <option value="STATIC">{t('outputFormats.conditionalColumns.staticValue')}</option>
                        <option value="FIELD">{t('outputFormats.conditionalColumns.fieldValue')}</option>
                        <option value="CONCATENATED">{t('outputFormats.conditionalColumns.concatenatedValue')}</option>
                      </select>
                      {output.type === 'STATIC' && (
                        <input
                          type="text"
                          className="form-input config-input"
                          value={output.value || ''}
                          onChange={(e) => updateBranchCb({ value: e.target.value })}
                          placeholder={t('outputFormats.conditionalColumns.staticValue')}
                        />
                      )}
                      {output.type === 'FIELD' && (() => {
                        const branchTransform = getBranchTransformType(output);
                        const resetTransformFields: Partial<BranchOutput> = {
                          enumMappingRef: undefined, regexPattern: undefined, regexReplacement: undefined,
                          dateFormat: undefined, numberFormat: undefined, textTransform: undefined,
                        };
                        return (
                          <>
                            <select
                              className="form-select form-select-sm"
                              value={output.value || ''}
                              onChange={(e) => updateBranchCb({ value: e.target.value })}
                            >
                              <option value="">-- {t('outputFormats.conditionalColumns.selectField') || 'Select field'} --</option>
                              {sourceFieldOptions.map((f) => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                              ))}
                            </select>
                            {/* Per-branch transform dropdown for FIELD outputs */}
                            <select
                              className="form-select form-select-sm"
                              value={branchTransform || ''}
                              onChange={(e) => {
                                // Orphan cleanup: remove old enum mapping if switching away
                                if (output.enumMappingRef) {
                                  const mappingId = output.enumMappingRef;
                                  const isReferencedElsewhere =
                                    columns.some((c) => c.enumMappingRef === mappingId) ||
                                    concatenatedColumns.some((cc) =>
                                      cc.parts.some((p) => p.enumMappingRef === mappingId)
                                    );
                                  if (!isReferencedElsewhere) {
                                    setEnumMappings((prev) => prev.filter((m) => m.mappingId !== mappingId));
                                  }
                                }
                                const newTransform = e.target.value;
                                if (newTransform === 'ENUM_MAP') {
                                  const id = `mapping_${Date.now()}`;
                                  setEnumMappings((prev) => [...prev, {
                                    mappingId: id,
                                    name: output.value || col.name || 'Cond Branch',
                                    fallback: null,
                                    entries: [] as { from: string; to: string }[],
                                  }]);
                                  updateBranchCb({ ...resetTransformFields, enumMappingRef: id });
                                } else if (newTransform === 'TEXT_TRANSFORM') {
                                  updateBranchCb({ ...resetTransformFields, textTransform: 'UPPERCASE' });
                                } else if (newTransform === 'TEXT_REPLACE') {
                                  updateBranchCb({ ...resetTransformFields, regexPattern: '', regexReplacement: '\\1' });
                                } else if (newTransform === 'DATE_FORMAT') {
                                  updateBranchCb({ ...resetTransformFields, dateFormat: 'DD/MM/YYYY' });
                                } else if (newTransform === 'NUMBER_FORMAT') {
                                  updateBranchCb({ ...resetTransformFields, numberFormat: '1,234.56' });
                                } else {
                                  updateBranchCb(resetTransformFields);
                                }
                              }}
                              style={{ width: '130px', minWidth: '120px' }}
                              title={t('outputFormats.concatenatedColumns.noTransform')}
                            >
                              <option value="">{t('outputFormats.concatenatedColumns.noTransform')}</option>
                              <option value="ENUM_MAP">{t('outputFormats.transforms.ENUM_MAP')}</option>
                              <option value="TEXT_REPLACE">{t('outputFormats.transforms.TEXT_REPLACE')}</option>
                              <option value="TEXT_TRANSFORM">{t('outputFormats.transforms.TEXT_TRANSFORM')}</option>
                              <option value="DATE_FORMAT">{t('outputFormats.transforms.DATE_FORMAT')}</option>
                              <option value="NUMBER_FORMAT">{t('outputFormats.transforms.NUMBER_FORMAT')}</option>
                            </select>
                            {/* Inline transform config */}
                            {branchTransform === 'TEXT_TRANSFORM' && (
                              <select
                                className="form-select form-select-sm"
                                value={output.textTransform || 'UPPERCASE'}
                                onChange={(e) => updateBranchCb({ textTransform: e.target.value })}
                                style={{ width: '70px', minWidth: '65px' }}
                              >
                                <option value="UPPERCASE">ABC</option>
                                <option value="LOWERCASE">abc</option>
                              </select>
                            )}
                            {branchTransform === 'DATE_FORMAT' && (
                              <select
                                className="form-select form-select-sm"
                                value={output.dateFormat || 'DD/MM/YYYY'}
                                onChange={(e) => updateBranchCb({ dateFormat: e.target.value })}
                                style={{ width: '100px' }}
                              >
                                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                                <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                                <option value="YYYYMMDD">YYYYMMDD</option>
                                <option value="DDMMYYYY">DDMMYYYY</option>
                              </select>
                            )}
                            {branchTransform === 'NUMBER_FORMAT' && (
                              <select
                                className="form-select form-select-sm"
                                value={output.numberFormat || '1,234.56'}
                                onChange={(e) => updateBranchCb({ numberFormat: e.target.value })}
                                style={{ width: '85px' }}
                              >
                                <option value="1,234.56">1,234.56</option>
                                <option value="1.234,56">1.234,56</option>
                                <option value="1234.56">1234.56</option>
                                <option value="1234">1234</option>
                              </select>
                            )}
                          </>
                        );
                      })()}
                      {output.type === 'CONCATENATED' && (
                        <>
                          <input
                            type="text"
                            className="form-input config-input"
                            value={output.separator || ''}
                            onChange={(e) => updateBranchCb({ separator: e.target.value })}
                            placeholder={t('outputFormats.concatenatedColumns.separator')}
                            title={t('outputFormats.concatenatedColumns.separatorHint')}
                            style={{ width: '65px', maxWidth: '75px' }}
                          />
                          {(output.parts || []).map((part, pIdx) => {
                            const partTransform = getPartTransformType(part);
                            return (
                              <div key={pIdx} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 6px', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: '4px' }}>
                                <select
                                  className="form-select form-select-sm"
                                  value={part.type}
                                  onChange={(e) => updateConcatPart(pIdx, { type: e.target.value as ConcatenatedPart['type'], sourceField: undefined, staticValue: undefined })}
                                  style={{ width: '100px', minWidth: '95px' }}
                                >
                                  <option value="FIELD">{t('outputFormats.concatenatedColumns.field')}</option>
                                  <option value="STATIC">{t('outputFormats.concatenatedColumns.static')}</option>
                                </select>
                                {part.type === 'FIELD' ? (
                                  <select
                                    className="form-select form-select-sm"
                                    value={part.sourceField || ''}
                                    onChange={(e) => updateConcatPart(pIdx, { sourceField: e.target.value })}
                                    style={{ width: '140px', minWidth: '120px' }}
                                  >
                                    <option value="">--</option>
                                    {sourceFieldOptions.map((o) => (
                                      <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    type="text"
                                    className="form-input config-input"
                                    value={part.staticValue || ''}
                                    onChange={(e) => updateConcatPart(pIdx, { staticValue: e.target.value })}
                                    placeholder={t('outputFormats.concatenatedColumns.staticText')}
                                    style={{ width: '100px', minWidth: '80px' }}
                                  />
                                )}
                                {/* Per-part transform dropdown */}
                                <select
                                  className="form-select form-select-sm"
                                  value={partTransform || ''}
                                  onChange={(e) => {
                                    const resetFields: Partial<ConcatenatedPart> = {
                                      enumMappingRef: undefined, regexPattern: undefined, regexReplacement: undefined,
                                      dateFormat: undefined, numberFormat: undefined, textTransform: undefined,
                                    };
                                    // Orphan cleanup: remove old enum mapping if switching away
                                    if (part.enumMappingRef) {
                                      const mappingId = part.enumMappingRef;
                                      const isReferencedElsewhere =
                                        columns.some((c) => c.enumMappingRef === mappingId) ||
                                        concatenatedColumns.some((cc) =>
                                          cc.parts.some((p) => p.enumMappingRef === mappingId)
                                        );
                                      if (!isReferencedElsewhere) {
                                        setEnumMappings((prev) => prev.filter((m) => m.mappingId !== mappingId));
                                      }
                                    }
                                    const newTransform = e.target.value;
                                    if (newTransform === 'ENUM_MAP') {
                                      const id = `mapping_${Date.now()}`;
                                      setEnumMappings((prev) => [...prev, {
                                        mappingId: id,
                                        name: part.sourceField || col.name || 'Cond Part',
                                        fallback: null,
                                        entries: [] as { from: string; to: string }[],
                                      }]);
                                      updateConcatPart(pIdx, { ...resetFields, enumMappingRef: id });
                                    } else if (newTransform === 'TEXT_TRANSFORM') {
                                      updateConcatPart(pIdx, { ...resetFields, textTransform: 'UPPERCASE' });
                                    } else if (newTransform === 'TEXT_REPLACE') {
                                      updateConcatPart(pIdx, { ...resetFields, regexPattern: '', regexReplacement: '\\1' });
                                    } else if (newTransform === 'DATE_FORMAT') {
                                      updateConcatPart(pIdx, { ...resetFields, dateFormat: 'DD/MM/YYYY' });
                                    } else if (newTransform === 'NUMBER_FORMAT') {
                                      updateConcatPart(pIdx, { ...resetFields, numberFormat: '1,234.56' });
                                    } else {
                                      updateConcatPart(pIdx, resetFields);
                                    }
                                  }}
                                  style={{ width: '130px', minWidth: '120px' }}
                                  title={t('outputFormats.concatenatedColumns.noTransform')}
                                >
                                  <option value="">{t('outputFormats.concatenatedColumns.noTransform')}</option>
                                  <option value="ENUM_MAP">{t('outputFormats.transforms.ENUM_MAP')}</option>
                                  <option value="TEXT_REPLACE">{t('outputFormats.transforms.TEXT_REPLACE')}</option>
                                  <option value="TEXT_TRANSFORM">{t('outputFormats.transforms.TEXT_TRANSFORM')}</option>
                                  <option value="DATE_FORMAT">{t('outputFormats.transforms.DATE_FORMAT')}</option>
                                  <option value="NUMBER_FORMAT">{t('outputFormats.transforms.NUMBER_FORMAT')}</option>
                                </select>
                                {/* Inline transform config */}
                                {partTransform === 'TEXT_TRANSFORM' && (
                                  <select
                                    className="form-select form-select-sm"
                                    value={part.textTransform || 'UPPERCASE'}
                                    onChange={(e) => updateConcatPart(pIdx, { textTransform: e.target.value })}
                                    style={{ width: '70px', minWidth: '65px' }}
                                  >
                                    <option value="UPPERCASE">ABC</option>
                                    <option value="LOWERCASE">abc</option>
                                  </select>
                                )}
                                {partTransform === 'DATE_FORMAT' && (
                                  <select
                                    className="form-select form-select-sm"
                                    value={part.dateFormat || 'DD/MM/YYYY'}
                                    onChange={(e) => updateConcatPart(pIdx, { dateFormat: e.target.value })}
                                    style={{ width: '100px' }}
                                  >
                                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                                    <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                                    <option value="YYYYMMDD">YYYYMMDD</option>
                                    <option value="DDMMYYYY">DDMMYYYY</option>
                                  </select>
                                )}
                                {partTransform === 'NUMBER_FORMAT' && (
                                  <select
                                    className="form-select form-select-sm"
                                    value={part.numberFormat || '1,234.56'}
                                    onChange={(e) => updateConcatPart(pIdx, { numberFormat: e.target.value })}
                                    style={{ width: '85px' }}
                                  >
                                    <option value="1,234.56">1,234.56</option>
                                    <option value="1.234,56">1.234,56</option>
                                    <option value="1234.56">1234.56</option>
                                    <option value="1234">1234</option>
                                  </select>
                                )}
                                {(output.parts || []).length > 1 && (
                                  <button
                                    className="btn-icon btn-sm"
                                    onClick={() => removeConcatPart(pIdx)}
                                    type="button"
                                    title={t('outputFormats.concatenatedColumns.removePart')}
                                    style={{ padding: '0 4px', lineHeight: 1 }}
                                  >
                                    &times;
                                  </button>
                                )}
                              </div>
                            );
                          })}
                          <button
                            className="btn-secondary btn-sm"
                            onClick={addConcatPart}
                            type="button"
                            style={{ padding: '2px 8px' }}
                          >
                            +
                          </button>
                          {/* TEXT_REPLACE panels for parts that have it active — rendered full-width below the parts row */}
                          {(output.parts || []).map((part, pIdx) => {
                            if (getPartTransformType(part) !== 'TEXT_REPLACE') return null;
                            return (
                              <div key={`tr-${pIdx}`} style={{ flexBasis: '100%', padding: '6px', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: '4px', marginTop: '4px' }}>
                                <div style={{ fontSize: '11px', marginBottom: '4px', fontWeight: 500, color: 'var(--text-muted, #666)' }}>
                                  {t('outputFormats.transforms.TEXT_REPLACE')} — {part.type === 'FIELD' ? (part.sourceField || `#${pIdx + 1}`) : (part.staticValue || `#${pIdx + 1}`)}:
                                </div>
                                <TextReplaceBuilder
                                  regexPattern={part.regexPattern || ''}
                                  regexReplacement={part.regexReplacement || ''}
                                  onChange={(pattern, replacement) => updateConcatPart(pIdx, { regexPattern: pattern, regexReplacement: replacement })}
                                />
                              </div>
                            );
                          })}
                          {/* ENUM_MAP editors for parts that have it active — rendered full-width below the parts row */}
                          {(output.parts || []).map((part, pIdx) => {
                            if (getPartTransformType(part) !== 'ENUM_MAP' || !part.enumMappingRef) return null;
                            const mapping = enumMappings.find((m) => m.mappingId === part.enumMappingRef);
                            if (!mapping) return null;

                            // Resolve field name to look up known enums
                            const rawFieldName = (part.sourceField || '').startsWith('trade:')
                              ? (part.sourceField || '').slice(6)
                              : (part.sourceField || '');
                            const enumKey = fieldAliases[rawFieldName] || rawFieldName;
                            const knownEnumValues = allEnums[enumKey];

                            if (knownEnumValues) {
                              const entryLookup: Record<string, string> = {};
                              mapping.entries.forEach((e) => {
                                if (e.from) entryLookup[e.from] = e.to;
                              });

                              return (
                                <div key={`em-${pIdx}`} style={{ flexBasis: '100%', padding: '6px', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: '4px', marginTop: '4px' }}>
                                  <div style={{ fontSize: '11px', marginBottom: '4px', fontWeight: 500, color: 'var(--text-muted, #666)' }}>
                                    {t('outputFormats.transforms.ENUM_MAP')} — {part.sourceField || `#${pIdx + 1}`}:
                                  </div>
                                  <div className="of-enum-table">
                                    <div className="of-enum-header">
                                      <span>{t('outputFormats.enumMapping.fxPlusValue')}</span>
                                      <span>{t('outputFormats.enumMapping.outputValue')}</span>
                                    </div>
                                    {knownEnumValues.map((enumVal) => (
                                      <div key={enumVal.value} className="of-enum-row">
                                        <span className="of-enum-fxplus">
                                          {enumVal.label} <span className="of-enum-code">({enumVal.value})</span>
                                        </span>
                                        <input
                                          type="text"
                                          className="form-input config-input"
                                          value={entryLookup[enumVal.value] || ''}
                                          onChange={(e) => {
                                            const newEntries = knownEnumValues.map((ev) => ({
                                              from: ev.value,
                                              to: ev.value === enumVal.value ? e.target.value : (entryLookup[ev.value] || ''),
                                            }));
                                            const knownFroms = new Set(knownEnumValues.map((ev) => ev.value));
                                            const extraEntries = mapping.entries.filter((entry) => !knownFroms.has(entry.from));
                                            updateEnumMapping({ ...mapping, entries: [...newEntries, ...extraEntries] });
                                          }}
                                          placeholder={t('outputFormats.enumMapping.outputValuePlaceholder')}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            }

                            // Fallback: free-form EnumMappingEditor for fields without known enum values
                            return (
                              <div key={`em-${pIdx}`} style={{ flexBasis: '100%', padding: '6px', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: '4px', marginTop: '4px' }}>
                                <div style={{ fontSize: '11px', marginBottom: '4px', fontWeight: 500, color: 'var(--text-muted, #666)' }}>
                                  {t('outputFormats.transforms.ENUM_MAP')} — {part.sourceField || `#${pIdx + 1}`}:
                                </div>
                                <EnumMappingEditor
                                  mapping={mapping}
                                  onChange={(updated) => updateEnumMapping(updated)}
                                  onClose={() => {}}
                                />
                              </div>
                            );
                          })}
                        </>
                      )}
                      {/* TEXT_REPLACE panel for FIELD branch outputs — rendered full-width below the field row */}
                      {output.type === 'FIELD' && getBranchTransformType(output) === 'TEXT_REPLACE' && (
                        <div style={{ flexBasis: '100%', padding: '6px', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: '4px', marginTop: '4px' }}>
                          <div style={{ fontSize: '11px', marginBottom: '4px', fontWeight: 500, color: 'var(--text-muted, #666)' }}>
                            {t('outputFormats.transforms.TEXT_REPLACE')} — {output.value || t(labelKey)}:
                          </div>
                          <TextReplaceBuilder
                            regexPattern={output.regexPattern || ''}
                            regexReplacement={output.regexReplacement || ''}
                            onChange={(pattern, replacement) => updateBranchCb({ regexPattern: pattern, regexReplacement: replacement })}
                          />
                        </div>
                      )}
                      {/* ENUM_MAP editor for FIELD branch outputs — rendered full-width below the field row */}
                      {output.type === 'FIELD' && getBranchTransformType(output) === 'ENUM_MAP' && output.enumMappingRef && (() => {
                        const mapping = enumMappings.find((m) => m.mappingId === output.enumMappingRef);
                        if (!mapping) return null;
                        const rawFieldName = (output.value || '').startsWith('trade:')
                          ? (output.value || '').slice(6)
                          : (output.value || '');
                        const enumKey = fieldAliases[rawFieldName] || rawFieldName;
                        const knownEnumValues = allEnums[enumKey];
                        if (knownEnumValues) {
                          const entryLookup: Record<string, string> = {};
                          mapping.entries.forEach((e) => { if (e.from) entryLookup[e.from] = e.to; });
                          return (
                            <div style={{ flexBasis: '100%', padding: '6px', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: '4px', marginTop: '4px' }}>
                              <div style={{ fontSize: '11px', marginBottom: '4px', fontWeight: 500, color: 'var(--text-muted, #666)' }}>
                                {t('outputFormats.transforms.ENUM_MAP')} — {output.value || t(labelKey)}:
                              </div>
                              <div className="of-enum-table">
                                <div className="of-enum-header">
                                  <span>{t('outputFormats.enumMapping.fxPlusValue')}</span>
                                  <span>{t('outputFormats.enumMapping.outputValue')}</span>
                                </div>
                                {knownEnumValues.map((enumVal) => (
                                  <div key={enumVal.value} className="of-enum-row">
                                    <span className="of-enum-fxplus">
                                      {enumVal.label} <span className="of-enum-code">({enumVal.value})</span>
                                    </span>
                                    <input
                                      type="text"
                                      className="form-input config-input"
                                      value={entryLookup[enumVal.value] || ''}
                                      onChange={(e) => {
                                        const newEntries = knownEnumValues.map((ev) => ({
                                          from: ev.value,
                                          to: ev.value === enumVal.value ? e.target.value : (entryLookup[ev.value] || ''),
                                        }));
                                        const knownFroms = new Set(knownEnumValues.map((ev) => ev.value));
                                        const extraEntries = mapping.entries.filter((entry) => !knownFroms.has(entry.from));
                                        updateEnumMapping({ ...mapping, entries: [...newEntries, ...extraEntries] });
                                      }}
                                      placeholder={t('outputFormats.enumMapping.outputValuePlaceholder')}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div style={{ flexBasis: '100%', padding: '6px', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: '4px', marginTop: '4px' }}>
                            <div style={{ fontSize: '11px', marginBottom: '4px', fontWeight: 500, color: 'var(--text-muted, #666)' }}>
                              {t('outputFormats.transforms.ENUM_MAP')} — {output.value || t(labelKey)}:
                            </div>
                            <EnumMappingEditor
                              mapping={mapping}
                              onChange={(updated) => updateEnumMapping(updated)}
                              onClose={() => {}}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              };

        return (
          <div className="transform-config-editor">
            {/* IF / ELSE IF branches */}
            {config.branches.map((branch, bIdx) => (
              <div key={bIdx} className="cond-branch-group" style={{ borderLeft: '3px solid var(--primary-color, #4a90d9)', paddingLeft: '10px', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <strong style={{ fontSize: '13px' }}>
                    {bIdx === 0 ? 'IF' : t('outputFormats.conditionalColumns.elseIf', 'ELSE IF')}
                  </strong>
                  {bIdx > 0 && (
                    <button
                      type="button"
                      className="btn-icon btn-sm"
                      onClick={() => removeBranch(bIdx)}
                      title={t('outputFormats.conditionalColumns.removeBranch', 'Remove branch')}
                      style={{ padding: '0 4px', lineHeight: 1, color: 'var(--text-danger, #d9534f)' }}
                    >
                      &times;
                    </button>
                  )}
                </div>
                {renderConditionControls(branch, bIdx)}
                {renderBranchOutput(
                  'outputFormats.conditionalColumns.thenOutput',
                  branch.output,
                  (updates) => updateBranchAtIndex(bIdx, { output: { ...branch.output, ...updates } }),
                  (newOutput) => updateBranchAtIndex(bIdx, { output: newOutput }),
                )}
              </div>
            ))}

            {/* Add ELSE IF button */}
            <button
              type="button"
              className="btn btn-sm btn-outline"
              onClick={addBranch}
              style={{ marginBottom: '10px' }}
            >
              + {t('outputFormats.conditionalColumns.addElseIf', 'Add ELSE IF')}
            </button>

            {/* ELSE output */}
            <div className="cond-branch-group" style={{ borderLeft: '3px solid var(--text-muted, #999)', paddingLeft: '10px', marginBottom: '12px' }}>
              <div style={{ marginBottom: '6px' }}>
                <strong style={{ fontSize: '13px' }}>ELSE</strong>
              </div>
              {renderBranchOutput(
                'outputFormats.conditionalColumns.elseOutput',
                config.elseOutput,
                (updates) => updateCondConfig({ elseOutput: { ...config.elseOutput, ...updates } }),
                (newOutput) => updateCondConfig({ elseOutput: newOutput }),
              )}
            </div>

            {/* Output format (Number / Date / Text Replace / Text Transform) — KAN-217, KAN-267 */}
            <hr style={{ margin: '12px 0 8px', borderColor: 'var(--border-color, #e0e0e0)' }} />
            <div className="config-row">
              <label>{t('outputFormats.conditionalColumns.outputFormat')}:</label>
              <select
                className="form-select form-select-sm"
                value={
                  col.numberFormat ? 'NUMBER_FORMAT'
                  : col.dateFormat ? 'DATE_FORMAT'
                  : col.regexPattern !== undefined || col.regexReplacement !== undefined ? 'TEXT_REPLACE'
                  : col.textTransform ? 'TEXT_TRANSFORM'
                  : ''
                }
                onChange={(e) => {
                  const newType = e.target.value;
                  if (newType === 'NUMBER_FORMAT') {
                    updateColumn(idx, { numberFormat: '1,234.56', dateFormat: undefined, regexPattern: undefined, regexReplacement: undefined, textTransform: undefined });
                  } else if (newType === 'DATE_FORMAT') {
                    updateColumn(idx, { dateFormat: 'DD/MM/YYYY', numberFormat: undefined, regexPattern: undefined, regexReplacement: undefined, textTransform: undefined });
                  } else if (newType === 'TEXT_REPLACE') {
                    updateColumn(idx, { regexPattern: '', regexReplacement: '', numberFormat: undefined, dateFormat: undefined, textTransform: undefined });
                  } else if (newType === 'TEXT_TRANSFORM') {
                    updateColumn(idx, { textTransform: 'UPPERCASE', numberFormat: undefined, dateFormat: undefined, regexPattern: undefined, regexReplacement: undefined });
                  } else {
                    updateColumn(idx, { numberFormat: undefined, dateFormat: undefined, regexPattern: undefined, regexReplacement: undefined, textTransform: undefined });
                  }
                }}
              >
                <option value="">{t('outputFormats.conditionalColumns.outputFormatNone')}</option>
                <option value="NUMBER_FORMAT">{t('outputFormats.transforms.NUMBER_FORMAT')}</option>
                <option value="DATE_FORMAT">{t('outputFormats.transforms.DATE_FORMAT')}</option>
                <option value="TEXT_REPLACE">{t('outputFormats.transforms.TEXT_REPLACE')}</option>
                <option value="TEXT_TRANSFORM">{t('outputFormats.transforms.TEXT_TRANSFORM')}</option>
              </select>
            </div>

            {col.numberFormat && (
              <div className="config-row">
                <label>{t('outputFormats.columns.numberFormat')}:</label>
                <select
                  className="form-select form-select-sm"
                  value={col.numberFormat}
                  onChange={(e) => updateColumn(idx, { numberFormat: e.target.value || undefined })}
                >
                  <option value="1,234.56">1,234.56</option>
                  <option value="1.234,56">1.234,56</option>
                  <option value="1234.56">1234.56</option>
                  <option value="1234,56">1234,56</option>
                  <option value="1234">1234</option>
                </select>
              </div>
            )}

            {col.dateFormat && (
              <div className="config-row">
                <label>{t('outputFormats.columns.dateFormat')}:</label>
                <select
                  className="form-select form-select-sm"
                  value={col.dateFormat}
                  onChange={(e) => updateColumn(idx, { dateFormat: e.target.value || undefined })}
                >
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                  <option value="DD.MM.YYYY">DD.MM.YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="MM.DD.YYYY">MM.DD.YYYY</option>
                  <option value="YYYYMMDD">YYYYMMDD</option>
                  <option value="DDMMYYYY">DDMMYYYY</option>
                </select>
              </div>
            )}

            {(col.regexPattern !== undefined || col.regexReplacement !== undefined) && !col.numberFormat && !col.dateFormat && !col.textTransform && (
              <TextReplaceBuilder
                regexPattern={col.regexPattern || ''}
                regexReplacement={col.regexReplacement || ''}
                onChange={(pattern, replacement) => updateColumn(idx, { regexPattern: pattern, regexReplacement: replacement })}
              />
            )}

            {col.textTransform && !col.numberFormat && !col.dateFormat && (
              <div className="transform-config-editor">
                <div className="config-row">
                  <label>{t('outputFormats.transforms.TEXT_TRANSFORM')}:</label>
                  <select
                    className="form-select form-select-sm"
                    value={col.textTransform || 'UPPERCASE'}
                    onChange={(e) => updateColumn(idx, { textTransform: e.target.value })}
                  >
                    <option value="UPPERCASE">{t('outputFormats.transforms.uppercase')}</option>
                    <option value="LOWERCASE">{t('outputFormats.transforms.lowercase')}</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  // ========== Render ==========

  if (loading) {
    return (
      <div className="tab-loading">
        <div className="spinner" />
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="template-editor">
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal((prev) => ({ ...prev, isOpen: false }))}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />

      {/* Confirm modal */}
      {confirmModal.isOpen && (
        <div className="modal-overlay" onClick={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}>
          <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{confirmModal.title}</h2>
              <button className="close-button" onClick={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}>×</button>
            </div>
            <div className="modal-body">
              <p>{confirmModal.message}</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  setConfirmModal((prev) => ({ ...prev, isOpen: false }));
                  confirmModal.onConfirm();
                }}
              >
                {t('common.yes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {!embedded && <h3>{isEditMode ? t('outputFormats.editTemplate') : t('outputFormats.createTemplate')}</h3>}

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="form-errors">
          {validationErrors.map((err, i) => (
            <p key={i}>{err}</p>
          ))}
        </div>
      )}

      <div className="editor-split">
        {/* LEFT COLUMN: Settings + Columns */}
        <div className="editor-main">

          {/* Settings Section — compact single row (hidden in embedded mode) */}
          {!embedded && (
            <div className="editor-section settings-compact">
              <div className="settings-row">
                <div className="form-group settings-name">
                  <label>{t('outputFormats.fields.name')}</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={100}
                    placeholder={t('outputFormats.fields.name')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('outputFormats.fields.outputFormat')}</label>
                  <div className="format-selector">
                    {(['CSV', 'XLSX', 'JSON'] as OutputFormat[]).map((fmt) => (
                      <button
                        key={fmt}
                        className={`format-option ${outputFormat === fmt ? 'active' : ''}`}
                        onClick={() => setOutputFormat(fmt)}
                        type="button"
                      >
                        {fmt}
                      </button>
                    ))}
                  </div>
                </div>
                {sourceSchema?.groupingAvailable && (
                  <div className="form-group">
                    <label>{t('outputFormats.rowMode.label')}</label>
                    <div className="format-selector">
                      <button
                        className={`format-option ${rowMode === 'INDIVIDUAL' ? 'active' : ''}`}
                        onClick={() => handleRowModeChange('INDIVIDUAL')}
                        type="button"
                        title={t('outputFormats.rowMode.individualDescription')}
                      >
                        {t('outputFormats.rowMode.individual')}
                      </button>
                      <button
                        className={`format-option ${rowMode === 'GROUPED' ? 'active' : ''}`}
                        onClick={() => handleRowModeChange('GROUPED')}
                        type="button"
                        title={t('outputFormats.rowMode.groupedDescription')}
                      >
                        {t('outputFormats.rowMode.grouped')}
                      </button>
                    </div>
                  </div>
                )}
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                  />
                  {t('outputFormats.fields.isDefault')}
                </label>
                {isEditMode && (
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                    {t('outputFormats.fields.isActive')}
                  </label>
                )}
              </div>
            </div>
          )}

          {/* CSV Options — collapsible (hidden in embedded mode) */}
          {!embedded && outputFormat === 'CSV' && (
            <div className="editor-section collapsible-section">
              <div
                className="collapsible-header"
                onClick={() => setCsvOptionsExpanded(!csvOptionsExpanded)}
              >
                <span className={`expand-icon ${csvOptionsExpanded ? 'expanded' : ''}`}>▶</span>
                <h4>{t('outputFormats.sections.csvOptions')}</h4>
              </div>
              {csvOptionsExpanded && (
                <div className="collapsible-body">
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('outputFormats.fields.delimiter')}</label>
                      <select
                        value={csvOptions.delimiter}
                        onChange={(e) => setCsvOptions({ ...csvOptions, delimiter: e.target.value })}
                      >
                        <option value=",">,</option>
                        <option value=";">;</option>
                        <option value="\t">Tab</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('outputFormats.fields.encoding')}</label>
                      <select
                        value={csvOptions.encoding}
                        onChange={(e) => setCsvOptions({ ...csvOptions, encoding: e.target.value })}
                      >
                        <option value="UTF-8">UTF-8</option>
                        <option value="ISO-8859-1">ISO-8859-1</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('outputFormats.fields.lineEnding')}</label>
                      <select
                        value={csvOptions.lineEnding}
                        onChange={(e) => setCsvOptions({ ...csvOptions, lineEnding: e.target.value as 'CRLF' | 'LF' })}
                      >
                        <option value="CRLF">CRLF</option>
                        <option value="LF">LF</option>
                      </select>
                    </div>
                  </div>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={csvOptions.includeHeader}
                      onChange={(e) => setCsvOptions({ ...csvOptions, includeHeader: e.target.checked })}
                    />
                    {t('outputFormats.fields.includeHeader')}
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Columns Section — TIC-style grid with inline transforms */}
          <div className="editor-section">
            <h4>{t('outputFormats.sections.columns')}</h4>

            {columns.length === 0 ? (
              <p className="of-empty-hint">{t('outputFormats.columns.noColumns')}</p>
            ) : (
              <div className="of-grid-table">
                <div className="of-grid-header">
                  <span></span>
                  <span>#</span>
                  <span>{t('outputFormats.columns.sourceField')}</span>
                  <span>{t('outputFormats.columns.outputName')}</span>
                  <span>{t('outputFormats.columns.transform')}</span>
                  <span></span>
                </div>
                {columns.map((col, idx) => {
                  const isConditional = !!col._condConfig;
                  const isStatic = col.sourceField.startsWith('static:');
                  const isConcatenated = col.sourceField.startsWith('concat:');
                  const concatColId = isConcatenated ? col.sourceField.slice(7) : null;
                  const concatCol = concatColId ? concatenatedColumns.find((c) => c.columnId === concatColId) : null;
                  const fieldDef = (isConditional || isStatic || isConcatenated) ? null : sourceFieldOptions.find((o) => o.value === col.sourceField);
                  const displaySource = isConditional
                    ? (col._condConfig?.conditionField
                        ? (sourceFieldOptions.find((o) => o.value === col._condConfig!.conditionField)?.label || col._condConfig!.conditionField)
                        : t('outputFormats.conditionalColumns.sectionTitle'))
                    : isStatic
                    ? t('outputFormats.staticColumn.label', 'Static Value')
                    : isConcatenated
                    ? (concatCol ? `${t('outputFormats.concatenatedColumns.sectionTitle')}: ${concatCol.parts.length} ${t('outputFormats.concatenatedColumns.parts').toLowerCase()}` : t('outputFormats.concatenatedColumns.sectionTitle'))
                    : (fieldDef?.label || col.sourceField);
                  const transformType = getColumnTransformType(col);
                  return (
                    <React.Fragment key={idx}>
                      <div
                        className={`of-grid-row ${expandedColumnIdx === idx ? 'expanded' : ''}`}
                        draggable
                        onDragStart={() => handleDragStart(idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDrop={handleDrop}
                      >
                        <span className="of-cell-drag"><span className="drag-handle">≡</span></span>
                        <span className="of-cell-order">{col.order}</span>
                        <span className={`of-cell-source ${isConditional ? 'of-cell-source-cond' : ''} ${isStatic ? 'of-cell-source-static' : ''} ${isConcatenated ? 'of-cell-source-cond' : ''}`}>
                          {isConditional && <span className="of-cond-badge">IF</span>}
                          {isConcatenated && <span className="of-cond-badge" style={{ background: '#6366f1' }}>ABC</span>}
                          {isStatic ? (
                            <>
                              <span className="of-static-badge">{t('outputFormats.staticColumn.badge', 'STATIC')}</span>
                              <input
                                type="text"
                                className="of-static-value-input"
                                value={col.sourceField.slice(7)}
                                onChange={(e) => updateColumn(idx, { sourceField: `static:${e.target.value}` })}
                                placeholder={t('outputFormats.staticColumn.placeholder', 'Fixed value (or leave blank)')}
                              />
                            </>
                          ) : (
                            displaySource
                          )}
                        </span>
                        <span className="of-cell-output">
                          <input
                            type="text"
                            value={col.outputName}
                            onChange={(e) => updateColumn(idx, { outputName: e.target.value })}
                            placeholder={t('outputFormats.columns.outputName')}
                          />
                        </span>
                        <span className="of-cell-transform">
                          {transformType ? (
                            <span
                              className={`of-transform-pill ${isConditional ? 'of-transform-pill-cond' : ''} ${isConcatenated ? 'of-transform-pill-cond' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedColumnIdx(expandedColumnIdx === idx ? null : idx);
                              }}
                            >
                              {t(`outputFormats.transforms.${transformType}`, transformType)}
                            </span>
                          ) : (
                            <button
                              className="btn-icon of-add-transform-btn"
                              title={t('outputFormats.columns.addTransform')}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedColumnIdx(idx);
                              }}
                              type="button"
                            >
                              +
                            </button>
                          )}
                        </span>
                        <span className="of-cell-actions">
                          <button
                            className="btn-icon btn-danger-icon"
                            onClick={() => removeColumn(idx)}
                            title={t('outputFormats.columns.remove')}
                            type="button"
                          >
                            ×
                          </button>
                        </span>
                      </div>

                      {/* Expanded transform config (TIC-style: type selector + config + footer) */}
                      {expandedColumnIdx === idx && (
                        <div className="of-grid-expand">
                          <div className="of-expand-header">
                            {isConditional ? (
                              <span className="of-expand-type-label">{t('outputFormats.conditionalColumns.sectionTitle')}</span>
                            ) : isConcatenated ? (
                              <span className="of-expand-type-label">{t('outputFormats.concatenatedColumns.sectionTitle')}</span>
                            ) : (
                              <select
                                className="form-select form-select-sm"
                                value={transformType || ''}
                                onChange={(e) => {
                                  if (e.target.value) {
                                    applyTransformType(idx, e.target.value);
                                  }
                                }}
                              >
                                {!transformType && (
                                  <option value="">{t('outputFormats.columns.selectTransform')}</option>
                                )}
                                {TRANSFORM_TYPE_KEYS.filter((k) => k !== 'CONDITIONAL').map((k) => (
                                  <option key={k} value={k}>
                                    {t(`outputFormats.transforms.${k}`, k)}
                                  </option>
                                ))}
                              </select>
                            )}
                            <button
                              className="btn-icon"
                              title={t('common.close')}
                              onClick={() => setExpandedColumnIdx(null)}
                            >
                              ▲
                            </button>
                          </div>
                          {transformType && renderTransformConfig(col, idx, transformType)}

                          {/* Concatenated column editor */}
                          {isConcatenated && concatCol && (() => {
                            const updatePart = (pIdx: number, updates: Partial<ConcatenatedPart>) => {
                              const newParts = [...concatCol.parts];
                              newParts[pIdx] = { ...newParts[pIdx], ...updates };
                              updateConcatenatedColumn(concatCol.columnId, { parts: newParts });
                              // Auto-enable trade enrichment when a trade: field is selected
                              const fieldsToCheck = [updates.sourceField, updates.conditionField].filter(Boolean) as string[];
                              for (const fv of fieldsToCheck) {
                                if (fv.startsWith('trade:')) {
                                  const tradeField = fv.slice(6);
                                  setTradeEnrichment((prev) => ({
                                    ...prev,
                                    enabled: true,
                                    fields: prev.fields.includes(tradeField) ? prev.fields : [...prev.fields, tradeField],
                                  }));
                                }
                              }
                            };
                            const clearPartTransform = (pIdx: number) => {
                              // Clean up orphaned enum mapping if switching away from ENUM_MAP
                              const oldPart = concatCol.parts[pIdx];
                              if (oldPart.enumMappingRef) {
                                const mappingId = oldPart.enumMappingRef;
                                // Check if any other column or concat part references this mapping
                                const isReferencedElsewhere =
                                  columns.some((c) => c.enumMappingRef === mappingId) ||
                                  concatenatedColumns.some((cc) =>
                                    cc.parts.some((p, i) => !(cc.columnId === concatCol.columnId && i === pIdx) && p.enumMappingRef === mappingId)
                                  );
                                if (!isReferencedElsewhere) {
                                  setEnumMappings((prev) => prev.filter((m) => m.mappingId !== mappingId));
                                }
                              }
                              return {
                                enumMappingRef: undefined, regexPattern: undefined, regexReplacement: undefined,
                                dateFormat: undefined, numberFormat: undefined, textTransform: undefined,
                              } as Partial<ConcatenatedPart>;
                            };
                            return (
                            <div className="transform-config-editor">
                              <div className="config-row">
                                <label>{t('outputFormats.concatenatedColumns.separator')}:</label>
                                <input
                                  type="text"
                                  className="form-input config-input"
                                  value={concatCol.separator}
                                  onChange={(e) => updateConcatenatedColumn(concatCol.columnId, { separator: e.target.value })}
                                  placeholder="(none)"
                                  style={{ width: '80px', maxWidth: '80px' }}
                                />
                                <span style={{ fontSize: '11px', color: 'var(--text-tertiary, #999)', marginLeft: '8px' }}>
                                  {t('outputFormats.concatenatedColumns.separatorHint')}
                                </span>
                              </div>
                              <div style={{ marginTop: '8px' }}>
                                <label style={{ fontWeight: 600, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                                  {t('outputFormats.concatenatedColumns.parts')}:
                                </label>
                                {concatCol.parts.map((part, pIdx) => {
                                  const partTransform = getPartTransformType(part);
                                  const needsCondValue = part.operator && OPERATORS_REQUIRING_VALUE.includes(part.operator as ConditionalOperator);
                                  return (
                                  <React.Fragment key={pIdx}>
                                  <div className="of-concat-part" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '6px', padding: '6px', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: '4px' }}>
                                    {/* Part type selector */}
                                    <select
                                      className="form-select form-select-sm"
                                      value={part.type}
                                      onChange={(e) => {
                                        const newType = e.target.value as ConcatenatedPart['type'];
                                        // Clean up orphaned mapping when switching part type
                                        if (part.enumMappingRef) {
                                          const mappingId = part.enumMappingRef;
                                          const isReferencedElsewhere =
                                            columns.some((c) => c.enumMappingRef === mappingId) ||
                                            concatenatedColumns.some((cc) =>
                                              cc.parts.some((p, i) => !(cc.columnId === concatCol.columnId && i === pIdx) && p.enumMappingRef === mappingId)
                                            );
                                          if (!isReferencedElsewhere) {
                                            setEnumMappings((prev) => prev.filter((m) => m.mappingId !== mappingId));
                                          }
                                        }
                                        const newParts = [...concatCol.parts];
                                        newParts[pIdx] = {
                                          type: newType,
                                          ...(newType === 'CONDITIONAL' ? { operator: 'IS_NOT_NULL', trueValue: '', falseValue: '' } : {}),
                                        };
                                        updateConcatenatedColumn(concatCol.columnId, { parts: newParts });
                                      }}
                                      style={{ width: '110px', minWidth: '110px' }}
                                    >
                                      <option value="FIELD">{t('outputFormats.concatenatedColumns.field')}</option>
                                      <option value="STATIC">{t('outputFormats.concatenatedColumns.static')}</option>
                                      <option value="CONDITIONAL">{t('outputFormats.concatenatedColumns.conditional')}</option>
                                    </select>

                                    {/* FIELD part: field picker + transform type + config */}
                                    {part.type === 'FIELD' && (
                                      <>
                                        <select
                                          className="form-select form-select-sm"
                                          value={part.sourceField || ''}
                                          onChange={(e) => updatePart(pIdx, { sourceField: e.target.value })}
                                          style={{ width: '160px', minWidth: '140px' }}
                                        >
                                          <option value="">-- {t('outputFormats.concatenatedColumns.selectField')} --</option>
                                          {sourceFieldOptions.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                          ))}
                                        </select>
                                        <select
                                          className="form-select form-select-sm"
                                          value={partTransform}
                                          onChange={(e) => {
                                            const nt = e.target.value;
                                            const cleared = clearPartTransform(pIdx);
                                            if (nt === 'ENUM_MAP') {
                                              const id = `mapping_${Date.now()}`;
                                              setEnumMappings((prev) => [...prev, {
                                                mappingId: id,
                                                name: part.sourceField || concatCol.name || 'Concat Part',
                                                fallback: null,
                                                entries: [] as { from: string; to: string }[],
                                              }]);
                                              cleared.enumMappingRef = id;
                                            }
                                            else if (nt === 'TEXT_REPLACE') { cleared.regexPattern = ''; cleared.regexReplacement = '\\1'; }
                                            else if (nt === 'TEXT_TRANSFORM') { cleared.textTransform = 'UPPERCASE'; }
                                            else if (nt === 'DATE_FORMAT') { cleared.dateFormat = 'DD/MM/YYYY'; }
                                            else if (nt === 'NUMBER_FORMAT') { cleared.numberFormat = '1,234.56'; }
                                            updatePart(pIdx, cleared);
                                          }}
                                          style={{ width: '155px', minWidth: '145px' }}
                                        >
                                          <option value="">{t('outputFormats.concatenatedColumns.noTransform')}</option>
                                          <option value="ENUM_MAP">{t('outputFormats.transforms.ENUM_MAP')}</option>
                                          <option value="TEXT_REPLACE">{t('outputFormats.transforms.TEXT_REPLACE')}</option>
                                          <option value="TEXT_TRANSFORM">{t('outputFormats.transforms.TEXT_TRANSFORM')}</option>
                                          <option value="DATE_FORMAT">{t('outputFormats.transforms.DATE_FORMAT')}</option>
                                          <option value="NUMBER_FORMAT">{t('outputFormats.transforms.NUMBER_FORMAT')}</option>
                                        </select>
                                        {/* Transform config (inline, non-block transforms) */}
                                        {partTransform === 'DATE_FORMAT' && (
                                          <select
                                            className="form-select form-select-sm"
                                            value={part.dateFormat || ''}
                                            onChange={(e) => updatePart(pIdx, { dateFormat: e.target.value || undefined })}
                                            style={{ width: '130px', minWidth: '120px' }}
                                          >
                                            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                                            <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                                            <option value="DD.MM.YYYY">DD.MM.YYYY</option>
                                            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                                            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                                            <option value="MM.DD.YYYY">MM.DD.YYYY</option>
                                            <option value="YYYYMMDD">YYYYMMDD</option>
                                            <option value="DDMMYYYY">DDMMYYYY</option>
                                          </select>
                                        )}
                                        {partTransform === 'NUMBER_FORMAT' && (
                                          <select
                                            className="form-select form-select-sm"
                                            value={part.numberFormat || ''}
                                            onChange={(e) => updatePart(pIdx, { numberFormat: e.target.value || undefined })}
                                            style={{ width: '110px', minWidth: '100px' }}
                                          >
                                            <option value="1,234.56">1,234.56</option>
                                            <option value="1.234,56">1.234,56</option>
                                            <option value="1234.56">1234.56</option>
                                            <option value="1234">1234</option>
                                          </select>
                                        )}
                                        {partTransform === 'TEXT_TRANSFORM' && (
                                          <select
                                            className="form-select form-select-sm"
                                            value={part.textTransform || 'UPPERCASE'}
                                            onChange={(e) => updatePart(pIdx, { textTransform: e.target.value })}
                                            style={{ width: '130px', minWidth: '120px' }}
                                          >
                                            <option value="UPPERCASE">{t('outputFormats.transforms.uppercase')}</option>
                                            <option value="LOWERCASE">{t('outputFormats.transforms.lowercase')}</option>
                                          </select>
                                        )}
                                      </>
                                    )}

                                    {/* STATIC part: text input */}
                                    {part.type === 'STATIC' && (
                                      <input
                                        type="text"
                                        className="form-input config-input"
                                        value={part.staticValue || ''}
                                        onChange={(e) => updatePart(pIdx, { staticValue: e.target.value })}
                                        placeholder={t('outputFormats.concatenatedColumns.staticText')}
                                        style={{ flex: 1 }}
                                      />
                                    )}

                                    {/* CONDITIONAL part: condition + then/else */}
                                    {part.type === 'CONDITIONAL' && (
                                      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '4px' }}>
                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                                          <select
                                            className="form-select form-select-sm"
                                            value={part.conditionField || ''}
                                            onChange={(e) => updatePart(pIdx, { conditionField: e.target.value })}
                                            style={{ width: '150px', minWidth: '120px' }}
                                          >
                                            <option value="">-- {t('outputFormats.concatenatedColumns.conditionField')} --</option>
                                            {sourceFieldOptions.map((f) => (
                                              <option key={f.value} value={f.value}>{f.label}</option>
                                            ))}
                                          </select>
                                          <select
                                            className="form-select form-select-sm"
                                            value={part.operator || 'IS_NOT_NULL'}
                                            onChange={(e) => updatePart(pIdx, { operator: e.target.value as ConditionalOperator })}
                                            style={{ width: '130px', minWidth: '110px' }}
                                          >
                                            {CONDITIONAL_OPERATORS.map((op) => (
                                              <option key={op} value={op}>{t(`outputFormats.conditionalColumns.operators.${op}`)}</option>
                                            ))}
                                          </select>
                                          {needsCondValue && (() => {
                                            const condRaw = (part.conditionField || '').startsWith('trade:')
                                              ? (part.conditionField || '').slice(6)
                                              : (part.conditionField || '');
                                            const condEnumKey = fieldAliases[condRaw] || condRaw;
                                            const condEnumVals = allEnums[condEnumKey];
                                            const isMultiPart = MULTI_VALUE_OPERATORS.includes((part.operator || '') as ConditionalOperator);
                                            const partSelectedValues = (part.conditionValue || '').split(',').filter(Boolean);
                                            const togglePartValue = (val: string) => {
                                              const set = new Set(partSelectedValues);
                                              if (set.has(val)) set.delete(val); else set.add(val);
                                              updatePart(pIdx, { conditionValue: Array.from(set).join(',') });
                                            };
                                            return isMultiPart && condEnumVals ? (
                                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', alignItems: 'center' }}>
                                                {condEnumVals.map((ev) => (
                                                  <label key={ev.value} style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '12px', cursor: 'pointer' }}>
                                                    <input
                                                      type="checkbox"
                                                      checked={partSelectedValues.includes(ev.value)}
                                                      onChange={() => togglePartValue(ev.value)}
                                                    />
                                                    {ev.label}
                                                  </label>
                                                ))}
                                              </div>
                                            ) : isMultiPart ? (
                                              <input
                                                type="text"
                                                className="form-input config-input"
                                                value={part.conditionValue || ''}
                                                onChange={(e) => updatePart(pIdx, { conditionValue: e.target.value })}
                                                placeholder={t('outputFormats.conditionalColumns.commaSeparatedValues')}
                                                style={{ width: '150px', maxWidth: '200px' }}
                                              />
                                            ) : condEnumVals ? (
                                              <select
                                                className="form-select form-select-sm"
                                                value={part.conditionValue || ''}
                                                onChange={(e) => updatePart(pIdx, { conditionValue: e.target.value })}
                                                style={{ width: '120px', minWidth: '100px' }}
                                              >
                                                <option value="">--</option>
                                                {condEnumVals.map((ev) => (
                                                  <option key={ev.value} value={ev.value}>{ev.label}</option>
                                                ))}
                                              </select>
                                            ) : (
                                              <input
                                                type="text"
                                                className="form-input config-input"
                                                value={part.conditionValue || ''}
                                                onChange={(e) => updatePart(pIdx, { conditionValue: e.target.value })}
                                                placeholder={t('outputFormats.concatenatedColumns.conditionValue')}
                                                style={{ width: '100px', maxWidth: '120px' }}
                                              />
                                            );
                                          })()}
                                        </div>
                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                          <label style={{ fontSize: '11px', fontWeight: 600, minWidth: 'fit-content' }}>
                                            {t('outputFormats.concatenatedColumns.thenValue')}:
                                          </label>
                                          <input
                                            type="text"
                                            className="form-input config-input"
                                            value={part.trueValue || ''}
                                            onChange={(e) => updatePart(pIdx, { trueValue: e.target.value })}
                                            style={{ flex: 1, minWidth: '60px' }}
                                          />
                                          <label style={{ fontSize: '11px', fontWeight: 600, minWidth: 'fit-content' }}>
                                            {t('outputFormats.concatenatedColumns.elseValue')}:
                                          </label>
                                          <input
                                            type="text"
                                            className="form-input config-input"
                                            value={part.falseValue || ''}
                                            onChange={(e) => updatePart(pIdx, { falseValue: e.target.value })}
                                            style={{ flex: 1, minWidth: '60px' }}
                                          />
                                        </div>
                                      </div>
                                    )}

                                    <button
                                      className="btn-icon btn-danger-icon"
                                      onClick={() => {
                                        // Clean up orphaned mapping when removing a part with enum ref
                                        if (part.enumMappingRef) {
                                          const mappingId = part.enumMappingRef;
                                          const isReferencedElsewhere =
                                            columns.some((c) => c.enumMappingRef === mappingId) ||
                                            concatenatedColumns.some((cc) =>
                                              cc.parts.some((p, i) => !(cc.columnId === concatCol.columnId && i === pIdx) && p.enumMappingRef === mappingId)
                                            );
                                          if (!isReferencedElsewhere) {
                                            setEnumMappings((prev) => prev.filter((m) => m.mappingId !== mappingId));
                                          }
                                        }
                                        const newParts = concatCol.parts.filter((_, i) => i !== pIdx);
                                        updateConcatenatedColumn(concatCol.columnId, { parts: newParts.length > 0 ? newParts : [{ type: 'FIELD', sourceField: '' }] });
                                      }}
                                      title={t('outputFormats.concatenatedColumns.removePart')}
                                      type="button"
                                      style={{ alignSelf: 'flex-start', marginTop: '2px' }}
                                    >
                                      ×
                                    </button>
                                  </div>
                                  {/* Text Replace editor below part controls */}
                                  {partTransform === 'TEXT_REPLACE' && (
                                    <div style={{ marginBottom: '6px', marginLeft: '16px', padding: '6px', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: '4px' }}>
                                      <TextReplaceBuilder
                                        regexPattern={part.regexPattern || ''}
                                        regexReplacement={part.regexReplacement || ''}
                                        onChange={(pattern, replacement) => updatePart(pIdx, { regexPattern: pattern, regexReplacement: replacement })}
                                      />
                                    </div>
                                  )}
                                  {/* Enum mapping editor below part controls */}
                                  {partTransform === 'ENUM_MAP' && part.enumMappingRef && (() => {
                                    const mapping = enumMappings.find((m) => m.mappingId === part.enumMappingRef);
                                    if (!mapping) return null;

                                    // Resolve field name to look up known enums
                                    const rawFieldName = (part.sourceField || '').startsWith('trade:')
                                      ? (part.sourceField || '').slice(6)
                                      : (part.sourceField || '');
                                    const enumKey = fieldAliases[rawFieldName] || rawFieldName;
                                    const knownEnumValues = allEnums[enumKey];

                                    if (knownEnumValues) {
                                      const entryLookup: Record<string, string> = {};
                                      mapping.entries.forEach((e) => {
                                        if (e.from) entryLookup[e.from] = e.to;
                                      });

                                      return (
                                        <div style={{ marginBottom: '6px', marginLeft: '16px', padding: '6px', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: '4px' }}>
                                          <div className="of-enum-table">
                                            <div className="of-enum-header">
                                              <span>{t('outputFormats.enumMapping.fxPlusValue')}</span>
                                              <span>{t('outputFormats.enumMapping.outputValue')}</span>
                                            </div>
                                            {knownEnumValues.map((enumVal) => (
                                              <div key={enumVal.value} className="of-enum-row">
                                                <span className="of-enum-fxplus">
                                                  {enumVal.label} <span className="of-enum-code">({enumVal.value})</span>
                                                </span>
                                                <input
                                                  type="text"
                                                  className="form-input config-input"
                                                  value={entryLookup[enumVal.value] || ''}
                                                  onChange={(e) => {
                                                    const newEntries = knownEnumValues.map((ev) => ({
                                                      from: ev.value,
                                                      to: ev.value === enumVal.value ? e.target.value : (entryLookup[ev.value] || ''),
                                                    }));
                                                    const knownFroms = new Set(knownEnumValues.map((ev) => ev.value));
                                                    const extraEntries = mapping.entries.filter((entry) => !knownFroms.has(entry.from));
                                                    updateEnumMapping({ ...mapping, entries: [...newEntries, ...extraEntries] });
                                                  }}
                                                  placeholder={t('outputFormats.enumMapping.outputValuePlaceholder')}
                                                />
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      );
                                    }

                                    // Fallback: generic from/to editor for fields without known enums
                                    return (
                                      <div style={{ marginBottom: '6px', marginLeft: '16px' }}>
                                        <EnumMappingEditor
                                          mapping={mapping}
                                          onChange={updateEnumMapping}
                                          onClose={() => {}}
                                        />
                                      </div>
                                    );
                                  })()}
                                  </React.Fragment>
                                  );
                                })}
                                <button
                                  className="btn-secondary btn-sm"
                                  onClick={() => {
                                    updateConcatenatedColumn(concatCol.columnId, {
                                      parts: [...concatCol.parts, { type: 'FIELD', sourceField: '' }],
                                    });
                                  }}
                                  type="button"
                                  style={{ marginTop: '4px' }}
                                >
                                  + {t('outputFormats.concatenatedColumns.addPart')}
                                </button>
                              </div>
                            </div>
                            );
                          })()}

                          <div className="of-expand-footer">
                            <button
                              className="btn-primary btn-sm"
                              onClick={() => setExpandedColumnIdx(null)}
                            >
                              {t('common.ok')}
                            </button>
                            {transformType && !isConditional && !isConcatenated && (
                              <button
                                className="btn-secondary btn-sm of-remove-transform-btn"
                                onClick={() => {
                                  removeTransformFromColumn(idx);
                                  setExpandedColumnIdx(null);
                                }}
                              >
                                {t('outputFormats.columns.removeTransform')}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            {/* Available fields — core, side, trade + Add Conditional Field button */}
            <div className="available-fields">
              {availableCoreFields.length > 0 && (
                <>
                  <label className="available-fields-label">
                    {rowMode === 'GROUPED' ? t('outputFormats.rowMode.sharedFields') : t('outputFormats.columns.availableFields')}
                  </label>
                  <div className="available-fields-pills">
                    {availableCoreFields.map((f) => (
                      <button
                        key={f.value}
                        className="available-field-pill"
                        onClick={() => addFieldAsColumn(f.value)}
                        title={t('outputFormats.columns.addToOutput')}
                        type="button"
                      >
                        + {f.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {availableSideFields.map((side) => (
                <React.Fragment key={side.sideName}>
                  <label className="available-fields-label available-fields-side">
                    {side.sideLabel} {t('outputFormats.rowMode.side') || 'side'}
                  </label>
                  <div className="available-fields-pills">
                    {side.fields.map((f) => (
                      <button
                        key={f.value}
                        className="available-field-pill available-field-pill-side"
                        onClick={() => addFieldAsColumn(f.value)}
                        title={t('outputFormats.columns.addToOutput')}
                        type="button"
                      >
                        + {f.label}
                      </button>
                    ))}
                  </div>
                </React.Fragment>
              ))}
              {availableTradeFields.length > 0 && (
                <>
                  <label className="available-fields-label available-fields-trade">{t('outputFormats.columns.tradeFields')}</label>
                  <div className="available-fields-pills">
                    {availableTradeFields.map((f) => (
                      <button
                        key={f.value}
                        className="available-field-pill available-field-pill-trade"
                        onClick={() => addFieldAsColumn(f.value)}
                        title={t('outputFormats.columns.addToOutput')}
                        type="button"
                      >
                        + {f.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {availableValuationFields.length > 0 && (
                <>
                  <label className="available-fields-label available-fields-trade">
                    {t('outputFormats.columns.valuationFields', 'Valuation Fields')}
                  </label>
                  <div className="available-fields-pills">
                    {availableValuationFields.map((f) => (
                      <button
                        key={f.value}
                        className="available-field-pill available-field-pill-trade"
                        onClick={() => addFieldAsColumn(f.value)}
                        title={t('outputFormats.columns.addToOutput')}
                        type="button"
                      >
                        + {f.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <div className="available-fields-actions">
                <button
                  className="btn-secondary btn-sm of-add-cond-btn"
                  onClick={addStaticColumn}
                  type="button"
                >
                  + {t('outputFormats.staticColumn.addStatic', 'Static Field')}
                </button>
                <button
                  className="btn-secondary btn-sm of-add-cond-btn"
                  onClick={addConditionalColumn}
                  type="button"
                >
                  + {t('outputFormats.conditionalColumns.addCondition')}
                </button>
                <button
                  className="btn-secondary btn-sm of-add-cond-btn"
                  onClick={addConcatenatedColumn}
                  type="button"
                >
                  + {t('outputFormats.concatenatedColumns.addConcatenated')}
                </button>
              </div>
            </div>
          </div>

          {/* Sort By section */}
          <div className="editor-section">
            <h4>{t('outputFormats.sortBy.label')}</h4>
            {sortBy.map((spec, sIdx) => {
              const dirLabels = getSortDirectionLabels(spec.field);
              return (
                <div key={sIdx} className="config-row" style={{ marginBottom: '6px' }}>
                  <select
                    className="form-select form-select-sm"
                    value={spec.field}
                    onChange={(e) => {
                      const updated = [...sortBy];
                      updated[sIdx] = { ...updated[sIdx], field: e.target.value };
                      setSortBy(updated);
                    }}
                    style={{ flex: 1 }}
                  >
                    <option value="">-- {t('outputFormats.sortBy.selectField')} --</option>
                    {sortFieldOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <select
                    className="form-select form-select-sm"
                    value={spec.direction}
                    onChange={(e) => {
                      const updated = [...sortBy];
                      updated[sIdx] = { ...updated[sIdx], direction: e.target.value as 'ASC' | 'DESC' };
                      setSortBy(updated);
                    }}
                    style={{ width: '120px', minWidth: '120px' }}
                  >
                    <option value="ASC">{dirLabels.asc}</option>
                    <option value="DESC">{dirLabels.desc}</option>
                  </select>
                  <button
                    type="button"
                    className="btn-danger btn-sm"
                    onClick={() => setSortBy(sortBy.filter((_, i) => i !== sIdx))}
                    title={t('outputFormats.sortBy.removeSort')}
                    style={{ padding: '2px 8px' }}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
            {sortBy.length < 3 && (
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => setSortBy([...sortBy, { field: '', direction: 'ASC' }])}
              >
                + {t('outputFormats.sortBy.addSort')}
              </button>
            )}
            {sortBy.length === 0 && (
              <p className="of-empty-hint" style={{ marginTop: '4px' }}>{t('outputFormats.sortBy.noSort')}</p>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: Live Preview */}
        <div className="editor-preview">
          <div className="editor-section">
            <h4>{t('outputFormats.sections.preview')}</h4>
            <div className="live-preview">
              {previewHeaders.length === 0 ? (
                <p className="of-empty-hint">{t('outputFormats.hints.addColumnsForPreview')}</p>
              ) : (
                <div className="preview-headers">
                  {previewHeaders.map((header, i) => (
                    <span key={i} className="preview-header-pill">
                      {header}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action bar (hidden in embedded mode) */}
      {!embedded && (
        <div className="editor-actions">
          <button className="btn-secondary" onClick={handleCancel} type="button">
            {t('outputFormats.cancel')}
          </button>
          {isEditMode && (
            <button
              className="btn-danger"
              onClick={handleDeactivate}
              disabled={saving}
              type="button"
            >
              {t('outputFormats.deleteTemplate')}
            </button>
          )}
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
            type="button"
          >
            {saving ? t('common.saving') : t('outputFormats.saveTemplate')}
          </button>
        </div>
      )}
    </div>
  );
});

export default OutputFormatsTemplateEditor;
